import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Users, Clock3, CalendarDays, WalletCards, Building2, Edit2, Trash2, X, Sparkles, Check, UserMinus, ChevronDown, ChevronRight, List, Activity, DollarSign, LayoutDashboard, HelpCircle } from 'lucide-react';
import StaffPerformance from './StaffPerformance';
import AttendanceGraphView from '../components/AttendanceGraphView';
import StaffPortalView from './StaffPortal';
import StaffTab from './StaffTab';
import ShopHoursTab from './ShopHoursTab';
import RosterTab from './RosterTab';
import StaffDashboardTab from './StaffDashboardTab';
import { renderStaffRecordRow } from './StaffRecordRow';
import { renderStaffRecordsCalendar } from './StaffRecordsCalendarView';
import { renderStaffRecordsTable } from './StaffRecordsTable';
import AddLeaveModal from '../components/AddLeaveModal';
import { LifecycleBadge, EventLegend, offDayFilled } from '../components/LifecycleBadge';
import useSortable from '../hooks/useSortable';
import useDateFilter from '../hooks/useDateFilter';
import SortableHeader from '../components/SortableHeader';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import { formatApiError } from '../utils/apiErrors';
import { getSalarySummaryForDisplayDate, getEffectiveSalaryDueSummary, isStaffActiveOnDate, getStaffWageTypeOnDate, buildStaffDropdownOptions, mergeStaffWithLifecycleHistory, roundSalaryDisplayAmount } from '../utils/payroll';
import { alignSalarySummaryRowsWithEvents, buildSalaryCalendarData, buildSalaryCycleRangeLabel, buildSalaryGrandTotal, buildSalaryMonthDueTotals, buildSalaryOverviewParams, buildSalaryStaffOptions, filterSalaryEventRows, filterSalarySummaryRows, formatSignedCarry, getSalaryCycleMonthReference } from '../utils/salaryOverview';
import { fetchPaginatedCollection } from '../utils/apiPagination';
import UiDateRangeBlock from '../components/ui/UiDateRangeBlock';
import { UiViewToggle } from '../components/ui/UiTabs';
import UiDateField from '../components/ui/UiDateField';
import UiPanel from '../components/ui/UiPanel';
import UiSearchField from '../components/ui/UiSearchField';
import UiTimeField from '../components/ui/UiTimeField';
import SalaryCycleSummary from '../components/SalaryCycleSummary';
import SalaryHelpModal from '../components/SalaryHelpModal';
import { buildStandardPresetOptions, getBusinessBoundaryTimes, resolveStandardStartEdit, shiftDateRangeByPreset, shouldKeepStandardPresetForEnd } from '../utils/dateRangeUi';
import { LIFECYCLE_MARKER_TYPES, ATTENDANCE_REASONS, splitPunchNotes, computeCalendarData, computeYearCalData, computeCurrentItems, computeDisplayItems, computeGroupedByDate } from '../utils/staffRecordsEngine';
import { formatUiCurrencyCompact, formatUiDate as formatDate, formatUiDateTime as formatDateTime, formatUiDateTimeRange } from '../utils/uiFormat';

const tabs = [
  { id: 'staff', label: 'Staff', icon: Users },
  { id: 'performance', label: 'Performance', icon: Activity },
  { id: 'attendance', label: 'Attendance', icon: Clock3 },
  { id: 'leave', label: 'Leave', icon: UserMinus },
  { id: 'salary_schedule', label: 'Salary', icon: WalletCards },
  { id: 'shop_hours', label: 'Shop Hours', icon: Building2 },
  { id: 'roster', label: 'Roster', icon: CalendarDays },
  { id: 'staff_dashboard', label: 'Dashboard', icon: LayoutDashboard },
];

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_ABBREVS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const getISOWeekStr = (d = new Date()) => {
  const date = new Date(d);
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  const w1 = new Date(date.getFullYear(), 0, 4);
  const wk = 1 + Math.round(((date - w1) / 86400000 - 3 + (w1.getDay() + 6) % 7) / 7);
  return `${date.getFullYear()}-W${String(wk).padStart(2, '0')}`;
};

const weekToMonday = (weekStr) => {
  if (!weekStr) return null;
  const [y, w] = weekStr.split('-W').map(Number);
  const jan4 = new Date(y, 0, 4);
  const adj = (jan4.getDay() + 6) % 7;
  const mon = new Date(jan4);
  mon.setDate(jan4.getDate() - adj + (w - 1) * 7);
  return mon;
};

const isoDate = (d) => d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : '';
const getStaffFullName = (member) => member?.full_name || `${member?.first_name || ''} ${member?.last_name || ''}`.trim();
const getStaffOptionLabel = (member, includeStatus = false, targetDate = null) => {
  // Wage type is time-based; when a date context exists, mark "· Daily" as it was on that date.
  const wt = targetDate ? getStaffWageTypeOnDate(member, targetDate) : member?.wage_type;
  return `${getStaffFullName(member)}${wt === 'daily_wages' ? ' · Daily' : ''}${includeStatus && member?.employment_status === 'terminated' ? ' (Terminated)' : ''}`;
};
// Pay-type identity colors (match the Add/Edit selector): salaried = blue, daily wages = amber.
const getWageTypeClass = (wageType) => (wageType === 'daily_wages' ? 'text-amber-700' : 'text-blue-700');
const getWageTypeLabel = (wageType) => (wageType === 'daily_wages' ? 'Daily Wages' : 'Salaried');
const getShortStaffName = (name) => String(name || '').trim().split(/\s+/)[0] || '';
const formatSalaryCurrency = (value) => formatUiCurrencyCompact(roundSalaryDisplayAmount(value));
const formatCompactK = (value) => `${(roundSalaryDisplayAmount(value) / 1000).toFixed(1).replace(/\.0$/, '')}K`;
const addCalendarDays = (dateStr, days) => {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return isoDate(date);
};
const getLocalDateString = (date = new Date()) => isoDate(date);
const getLocalDateTimeInputValue = (date = new Date()) => (
  `${getLocalDateString(date)}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
);
const getBusinessDateString = (businessDayStartHour = 6) => {
  const date = new Date();
  if (date.getHours() < businessDayStartHour) {
    date.setDate(date.getDate() - 1);
  }
  return isoDate(date);
};
const getBusinessDateForDateTimeValue = (value, businessDayStartHour = 6) => {
  if (!value) return '';
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  if (date.getHours() < businessDayStartHour) {
    date.setDate(date.getDate() - 1);
  }
  return isoDate(date);
};

const isStaffEligibleOnDate = isStaffActiveOnDate;

const initialShopHours = {
  effective_from: getLocalDateString(),
  open_time: '09:00',
  close_time: '18:00',
  notes: '',
  selectedDays: [0, 1, 2, 3, 4],
};

const initialPublicHoliday = {
  name: '',
  start_date: getBusinessDateString(),
  end_date: '',
  notes: '',
};

const initialStaff = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  address: '',
  department: '',
  designation: '',
  hire_date: getLocalDateString(),
  employment_status: 'active',
  monthly_salary: '',
  salary_date: '1',
  wage_type: 'daily_wages',
  wage_type_effective_date: '',
  notes: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  user: ''
};

const initialAttendance = {
  staff: '',
  punch_type: 'in',
  punch_time: getLocalDateTimeInputValue(),
  notes: ''
};

const initialLeave = {
  staff: '',
  is_paid: true,
  leave_type: 'casual',
  start_date: getBusinessDateString(),
  end_date: getBusinessDateString(),
  status: 'approved',
  reason: '',
  is_absent: false,
};

const getArrivalStatus = (punchTime, shiftStart) => {
  if (!shiftStart) return null;
  const punch = new Date(punchTime);
  const [sh, sm] = shiftStart.split(':').map(Number);
  const shiftDate = new Date(punch);
  shiftDate.setHours(sh, sm, 0, 0);
  const lateBy = (punch - shiftDate) / 60000;
  if (lateBy > 60) return 'half_day';
  if (lateBy > 15) return 'late';
  return 'on_time';
};

const initialRoster = {
  staff: '',
  roster_date: getLocalDateString(),
  shift_start: '09:00',
  shift_end: '18:00',
  notes: ''
};

const initialAdvance = {
  staff: '',
  advance_date: getLocalDateString(),
  amount: '',
  payment_type: 'advance',
  status: 'approved',
  repaid_amount: '0',
  reason: ''
};

const initialStaffEventForm = {
  source: 'financial',
  id: null,
  staff: '',
  event_type: 'salary',
  event_date: getLocalDateString(),
  amount: '',
  status: 'approved',
  reason: '',
  department: '',
  monthly_salary: '',
  salary_date: '',
  employment_status_value: 'active',
};

const FINANCIAL_EVENT_TYPES = ['salary', 'advance', 'bonus', 'gift', 'deduction'];
const DYNAMIC_EVENT_TYPES = ['status_change', 'department_change', 'salary_change', 'salary_date_change'];
const STAFF_EVENT_TYPE_OPTIONS = {
  financial: [
    { value: 'salary', label: 'Salary Paid' },
    { value: 'advance', label: 'Advance Taken' },
    { value: 'bonus', label: 'Bonus Paid' },
    { value: 'gift', label: '🎁 Gift Given' },
    { value: 'deduction', label: 'Deduction Made' },
  ],
  lifecycle: [
    { value: 'employed', label: 'Employed' },
    { value: 'reemployed', label: 'Re-employed' },
    { value: 'terminated', label: 'Terminated' },
    { value: 'status_change', label: 'Status Change' },
    { value: 'department_change', label: 'Department Change' },
    { value: 'salary_change', label: 'Salary Change' },
    { value: 'salary_date_change', label: 'Salary Due Day Change' },
  ],
};

const getEventSourceFromType = (eventType) => (
  FINANCIAL_EVENT_TYPES.includes(eventType) ? 'financial' : 'lifecycle'
);
const getStaffEventTypeLabel = (eventType) => (
  [...STAFF_EVENT_TYPE_OPTIONS.financial, ...STAFF_EVENT_TYPE_OPTIONS.lifecycle]
    .find(option => option.value === eventType)?.label || eventType
);

const fmtDate = (ds) => formatDate(ds);
const fmtDateTime = (ds) => formatDateTime(ds, { seconds: true });

const buildSelectedRangeLabel = (fromDate, fromTime, toDate, toTime) => (
  fromDate && toDate ? formatUiDateTimeRange(fromDate, fromTime, toDate, toTime) : ''
);

const splitDateTimeInputValue = (value, fallbackDate = '') => {
  if (!value) {
    return { date: fallbackDate, time: '' };
  }
  const [datePart, timePart = ''] = String(value).split('T');
  return {
    date: datePart || fallbackDate,
    time: timePart.slice(0, 5),
  };
};

const mergeDateTimeInputValue = (datePart, timePart, fallbackDate = '') => {
  const resolvedDate = datePart || fallbackDate;
  const resolvedTime = timePart || '00:00';
  return resolvedDate ? `${resolvedDate}T${resolvedTime}` : '';
};

function StaffManagement() {
  const { hasPermission, isAdmin } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const adminUser = isAdmin();
  const canViewStaff = adminUser || hasPermission('staff', 'view') || hasPermission('staff', 'add');
  const canViewAttendance = adminUser || hasPermission('attendance', 'view') || hasPermission('attendance', 'add');
  const canViewLeaves = adminUser || hasPermission('leaves', 'view') || hasPermission('leaves', 'add');
  const canManageLeaveViolations = adminUser || hasPermission('performance', 'edit');
  const getInitialTab = () => {
    if (canViewStaff) return 'staff';
    if (canViewAttendance) return 'attendance';
    if (canViewLeaves) return 'leave';
    return 'attendance';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [attRecordFilter, setAttRecordFilter] = useState('all');
  const [performanceStaffFilter, setPerformanceStaffFilter] = useState('');
  const [perfFocusRecord, setPerfFocusRecord] = useState(null);

  // Jump from a salary deduction row to its originating Performance record (table view).
  const openPerformanceRecord = (row) => {
    if (!row?.performanceId) return;
    setPerfFocusRecord({ id: row.performanceId, staffId: row.staffId, date: row.date, ts: Date.now() });
    setPerformanceStaffFilter(String(row.staffId));
    setActiveTab('performance');
  };
  const [dashboardStaffId, setDashboardStaffId] = useState('');
  const [businessDayStartHour, setBusinessDayStartHour] = useState(6);
  const {
    preset: datePreset,
    setPreset: setDatePreset,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    setCustomRange: setStaffCustomRange,
    shiftRange: shiftStaffDateRange,
    applyPreset,
  } = useDateFilter('today', businessDayStartHour);
  const businessStartTime = `${String(businessDayStartHour).padStart(2, '0')}:00`;
  const businessEndTime = `${String((businessDayStartHour + 23) % 24).padStart(2, '0')}:59`;
  const filterEndCalendarDate = endDate ? addCalendarDays(endDate, 1) : '';
  const currentBusinessDate = getBusinessDateString(businessDayStartHour);
  const restrictedTodayOnly = !adminUser && ['attendance', 'leave'].includes(activeTab);
  const [departments, setDepartments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaveEntries, setLeaveEntries] = useState([]);
  const [offDayRecords, setOffDayRecords] = useState([]);
  const [lifecycleEventsList, setLifecycleEventsList] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [shopHours, setShopHours] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [shopHoursSaving, setShopHoursSaving] = useState(false);
  const [shPreset, setShPreset] = useState('all');
  const [shCustomStart, setShCustomStart] = useState('');
  const [shCustomEnd, setShCustomEnd] = useState('');
  const [showShopHoursModal, setShowShopHoursModal] = useState(false);
  const [shopHoursListTab, setShopHoursListTab] = useState('hours');
  const [shopHoursModalTab, setShopHoursModalTab] = useState('hours');
  const [pendingShopHoursEntries, setPendingShopHoursEntries] = useState([]);
  const [editingShopHoursGroup, setEditingShopHoursGroup] = useState(null);
  const [holidayForm, setHolidayForm] = useState(initialPublicHoliday);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidaySaving, setHolidaySaving] = useState(false);
  const [attQuick, setAttQuick] = useState(() => { const now = new Date(); return { date: getLocalDateString(now), time: `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`, type: 'in', staff: '', notes: '' }; });
  const [attSaving, setAttSaving] = useState(false);
  const [leaveQuick, setLeaveQuick] = useState(() => ({ date: getBusinessDateString(), type: 'leave', leave_type: 'sick', staff: '', notes: '' }));
  const [leaveSaving, setLeaveSaving] = useState(false);
  const [rosterInline, setRosterInline] = useState({ week: getISOWeekStr(), days: [0,1,2,3,4], shift_start: '09:00', shift_end: '18:00', staff: '', allStaff: false, notes: '' });
  const [rosterSaving, setRosterSaving] = useState(false);
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [pendingRosterEntries, setPendingRosterEntries] = useState([]);
  const [pendingAttendanceEntries, setPendingAttendanceEntries] = useState([]);
  const [pendingLeaveEntries, setPendingLeaveEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [attendanceEntryType, setAttendanceEntryType] = useState('punch_in');
  const [leaveEntryType, setLeaveEntryType] = useState('leave');
  const [attView, setAttView] = useState('calendar');
  const [leaveView, setLeaveView] = useState('calendar');
  const [rosterView, setRosterView] = useState('calendar');
  const [perfAddTrigger, setPerfAddTrigger] = useState(0);
  const [perfPenaltyTrigger, setPerfPenaltyTrigger] = useState(0);
  const [advancesView, setAdvancesView] = useState('calendar');
  const [deductionsView, setDeductionsView] = useState('calendar');
  const [salaryView, setSalaryView] = useState('calendar');
  const [salaryPreset, setSalaryPreset] = useState('month');
  const [salaryCustomFrom, setSalaryCustomFrom] = useState('');
  const [salaryCustomTo, setSalaryCustomTo] = useState('');
  const [expandedDueRowId, setExpandedDueRowId] = useState(null);
  const [showAddLeave, setShowAddLeave] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [offDayEditRecord, setOffDayEditRecord] = useState(null);

  const handleDeleteLeave = async (leaveRecord) => {
    if (!leaveRecord?.id) return;
    if (!window.confirm('Delete this leave record?')) return;
    try {
      await api.delete(`/staff-leaves/${leaveRecord.id}/`);
      toast.success('Leave deleted');
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to delete leave'));
    }
  };
  const handleDeleteOffDay = async (record) => {
    if (!record?.id) return;
    const typeLabel = { leave: 'leave', holiday: 'holiday', absentee: 'absentee' }[record.violation_type] || 'off-day';
    if (!window.confirm(`Remove this ${typeLabel} record? Any related salary deduction will be reversed.`)) return;
    try {
      await api.delete(`/staff-performance/${record.id}/`);
      toast.success('Off-day removed');
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to remove off-day'));
    }
  };
  const [salaryRangeOverride, setSalaryRangeOverride] = useState(null);
  const [salaryStartTime, setSalaryStartTime] = useState('06:00');
  const [salaryEndTime, setSalaryEndTime] = useState('05:59');
  const [salaryCalMonth, setSalaryCalMonth] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() + 1 }; });
  const [salarySearch, setSalarySearch] = useState('');
  const [salaryEntryFilter, setSalaryEntryFilter] = useState('all');
  const [collapsedSalarySummaryStaff, setCollapsedSalarySummaryStaff] = useState(new Set());
  const [salaryOverview, setSalaryOverview] = useState({ summary: {}, staff_summary: [], event_rows: [] });
  const [salaryOverviewLoading, setSalaryOverviewLoading] = useState(false);
  const [showSalaryHelp, setShowSalaryHelp] = useState(false);
  const { sortKey: salarySortKey, sortDir: salarySortDir, handleSort: salaryHandleSort, sortData: salarySortData } = useSortable('date', 'desc');
  const [collapsedDates, setCollapsedDates] = useState(new Set());
  const [calMonthYM, setCalMonthYM] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}`; });

  useEffect(() => {
    const targetTab = searchParams.get('tab');
    const targetDate = searchParams.get('date');
    const targetView = searchParams.get('view');
    if (!targetTab && !targetDate && !targetView) return;

    if (targetTab === 'deductions') {
      setActiveTab('salary_schedule');
      setSalaryEntryFilter('deduction');
    } else if (targetTab && tabs.some(tab => tab.id === targetTab)) {
      setActiveTab(targetTab);
    }
    if (targetTab === 'salary_schedule') {
      setSalaryView(targetView === 'calendar' ? 'calendar' : 'table');
      if (targetDate) {
        setSalaryPreset('custom');
        setSalaryCustomFrom(targetDate);
        setSalaryCustomTo(targetDate);
        const d = new Date(`${targetDate}T12:00:00`);
        if (!Number.isNaN(d.getTime())) {
          setSalaryCalMonth({ y: d.getFullYear(), m: d.getMonth() + 1 });
        }
      }
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setSalaryStartTime(businessStartTime);
    setSalaryEndTime(businessEndTime);
  }, [businessStartTime, businessEndTime]);

  const getSalaryRangeForPreset = (preset, customFrom = salaryCustomFrom, customTo = salaryCustomTo) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const toIso = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (preset === 'today') return { from: toIso(today), to: toIso(today) };
    if (preset === 'week') {
      const dayOffset = (today.getDay() + 6) % 7;
      const start = new Date(today);
      start.setDate(today.getDate() - dayOffset);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { from: toIso(start), to: toIso(end) };
    }
    if (preset === 'month') {
      return { from: `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2, '0')}-01`, to: toIso(new Date(today.getFullYear(), today.getMonth()+1, 0)) };
    }
    if (preset === 'year') return { from: `${today.getFullYear()}-01-01`, to: `${today.getFullYear()}-12-31` };
    if (preset === 'custom') return { from: customFrom, to: customTo };
    return { from: '', to: '' };
  };

  const salaryFilterRange = useMemo(
    () => salaryRangeOverride || getSalaryRangeForPreset(salaryPreset),
    [salaryPreset, salaryCustomFrom, salaryCustomTo, salaryRangeOverride]
  );

  const getCurrentStaffCalendarView = () => (
    activeTab === 'attendance'
      ? attView
      : activeTab === 'leave'
        ? leaveView
        : activeTab === 'roster'
          ? rosterView
          : activeTab === 'deductions'
            ? deductionsView
            : activeTab === 'advances'
              ? advancesView
              : 'table'
  );
  const salaryFilterEndCalendarDate = salaryFilterRange.to ? addCalendarDays(salaryFilterRange.to, 1) : '';
  const staffSelectedRangeLabel = useMemo(
    () => buildSelectedRangeLabel(startDate, startTime, filterEndCalendarDate, endTime),
    [startDate, startTime, filterEndCalendarDate, endTime]
  );
  const salarySelectedRangeLabel = useMemo(
    () => buildSelectedRangeLabel(salaryFilterRange.from, salaryStartTime, salaryFilterEndCalendarDate, salaryEndTime),
    [salaryFilterRange.from, salaryStartTime, salaryFilterEndCalendarDate, salaryEndTime]
  );

  useEffect(() => {
    const cv = activeTab === 'attendance'
      ? attView
      : activeTab === 'leave'
        ? leaveView
        : activeTab === 'roster'
          ? rosterView
          : activeTab === 'deductions'
            ? deductionsView
            : activeTab === 'advances'
              ? advancesView
              : 'table';
    if (cv !== 'calendar' || ['month', 'year'].includes(datePreset)) return;
    const base = startDate ? new Date(`${startDate}T12:00:00`) : new Date();
    if (Number.isNaN(base.getTime())) return;
    const ym = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}`;
    const firstDay = `${ym}-01`;
    const lastDate = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    const lastDay = `${ym}-${String(lastDate).padStart(2, '0')}`;
    setCalMonthYM(ym);
    setStartDate(firstDay);
    setEndDate(lastDay);
    setDatePreset('month');
  }, [activeTab, attView, leaveView, rosterView, deductionsView, advancesView, datePreset, startDate]);

  useEffect(() => {
    if (salaryView !== 'calendar' || ['month', 'year'].includes(salaryPreset)) return;
    const base = salaryFilterRange.from ? new Date(`${salaryFilterRange.from}T12:00:00`) : new Date();
    if (Number.isNaN(base.getTime())) return;
    const ym = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}`;
    const firstDay = `${ym}-01`;
    const lastDate = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    const lastDay = `${ym}-${String(lastDate).padStart(2, '0')}`;
    setSalaryRangeOverride({ from: firstDay, to: lastDay });
    setSalaryCalMonth({ y: base.getFullYear(), m: base.getMonth() + 1 });
    setSalaryPreset('month');
  }, [salaryView, salaryPreset, salaryFilterRange.from]);

  const applyStaffDatePreset = (preset) => {
    if (getCurrentStaffCalendarView() === 'calendar' && ['month', 'year'].includes(preset)) {
      const [yr, mo] = calMonthYM.split('-').map(Number);
      const base = new Date(yr, (mo || 1) - 1, 1);
      const firstDay = preset === 'year'
        ? `${base.getFullYear()}-01-01`
        : `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-01`;
      const lastDay = preset === 'year'
        ? `${base.getFullYear()}-12-31`
        : `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
      setStartDate(firstDay);
      setEndDate(lastDay);
      setCalMonthYM(preset === 'year' ? `${base.getFullYear()}-01` : firstDay.substring(0, 7));
      setDatePreset(preset);
      return;
    }
    setDatePreset(preset);
    applyPreset(preset);
  };

  const handleStaffCalendarPeriodChange = ({ mode, startDate: periodStart, endDate: periodEnd, year, month }) => {
    setStartDate(periodStart);
    setEndDate(periodEnd);
    setCalMonthYM(`${year}-${String((mode === 'year' ? 0 : month) + 1).padStart(2, '0')}`);
    setDatePreset(mode);
  };

  const shiftStaffVisibleRange = (delta) => {
    if (getCurrentStaffCalendarView() !== 'calendar') {
      shiftStaffDateRange(delta);
      return;
    }
    const shifted = shiftDateRangeByPreset({
      preset: datePreset,
      startDate,
      endDate,
      delta,
    });
    setStartDate(shifted.startDate);
    setEndDate(shifted.endDate);
    setDatePreset(datePreset);
    if (shifted.startDate) {
      setCalMonthYM(shifted.startDate.substring(0, 7));
    }
  };

  const handleStaffStartDateChange = (value) => {
    setStartDate(value);
  };

  const handleStaffEndDateChange = (value) => {
    setEndDate(addCalendarDays(value, -1));
  };

  const handleSalaryStartDateChange = (value) => {
    const currentRange = getSalaryRangeForPreset(salaryPreset);
    const range = resolveStandardStartEdit({
      preset: salaryPreset,
      nextStartDate: value,
      startTime: salaryStartTime,
      endTime: salaryEndTime,
      businessDayStartHour,
    });
    if (range) {
      setSalaryRangeOverride({ from: range.startDate, to: range.endDate });
      setSalaryCalMonth({
        y: Number(range.startDate.substring(0, 4)),
        m: Number(range.startDate.substring(5, 7)),
      });
      return;
    }
    setSalaryCustomFrom(value);
    setSalaryCustomTo(salaryCustomTo || currentRange.to || value);
    setSalaryRangeOverride(null);
    setSalaryPreset('custom');
  };

  const handleSalaryEndDateChange = (value) => {
    const internalEndDate = addCalendarDays(value, -1);
    const currentRange = getSalaryRangeForPreset(salaryPreset);
    const rangeStart = salaryFilterRange.from || currentRange.from || internalEndDate;
    const keepPreset = shouldKeepStandardPresetForEnd({
      preset: salaryPreset,
      startDate: rangeStart,
      nextEndDate: internalEndDate,
      startTime: salaryStartTime,
      endTime: salaryEndTime,
      businessDayStartHour,
    });
    if (keepPreset) {
      setSalaryRangeOverride({ from: rangeStart, to: internalEndDate });
      return;
    }
    setSalaryCustomFrom(salaryCustomFrom || currentRange.from || internalEndDate);
    setSalaryCustomTo(internalEndDate);
    setSalaryRangeOverride(null);
    setSalaryPreset('custom');
  };

  const applySalaryDatePreset = (preset) => {
    if (salaryView === 'calendar' && ['month', 'year'].includes(preset)) {
      const year = salaryCalMonth.y;
      const month = Math.max(1, salaryCalMonth.m || 1);
      const firstDay = preset === 'year'
        ? `${year}-01-01`
        : `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = preset === 'year'
        ? `${year}-12-31`
        : `${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
      setSalaryPreset(preset);
      setSalaryRangeOverride({ from: firstDay, to: lastDay });
      setSalaryCalMonth({ y: year, m: preset === 'year' ? 1 : month });
      setSalaryStartTime(businessStartTime);
      setSalaryEndTime(businessEndTime);
      return;
    }
    setSalaryPreset(preset);
    setSalaryRangeOverride(null);
    if (preset !== 'custom') {
      setSalaryStartTime(businessStartTime);
      setSalaryEndTime(businessEndTime);
    }
  };

  const handleSalaryCalendarPeriodChange = ({ mode, startDate: periodStart, endDate: periodEnd, year, month }) => {
    setSalaryPreset(mode);
    setSalaryRangeOverride({ from: periodStart, to: periodEnd });
    setSalaryCalMonth({ y: year, m: mode === 'year' ? 1 : month + 1 });
    setSalaryStartTime(businessStartTime);
    setSalaryEndTime(businessEndTime);
  };

  const shiftSalaryDateRange = (delta) => {
    const currentRange = salaryFilterRange;
    const shifted = shiftDateRangeByPreset({
      preset: salaryPreset,
      startDate: currentRange.from,
      endDate: currentRange.to,
      delta,
    });
    setSalaryRangeOverride({ from: shifted.startDate, to: shifted.endDate });
    if (salaryView === 'calendar' && shifted.startDate) {
      const base = new Date(`${shifted.startDate}T12:00:00`);
      if (!Number.isNaN(base.getTime())) {
        setSalaryCalMonth({ y: base.getFullYear(), m: base.getMonth() + 1 });
      }
    }
  };

  const keepSalaryCustomRange = () => {
    const currentRange = getSalaryRangeForPreset(salaryPreset);
    setSalaryCustomFrom(salaryCustomFrom || currentRange.from);
    setSalaryCustomTo(salaryCustomTo || currentRange.to);
    setSalaryRangeOverride(null);
    setSalaryPreset('custom');
  };

  const handleSalaryStartTimeChange = (value) => {
    setSalaryStartTime(value);
    const boundary = getBusinessBoundaryTimes(businessDayStartHour);
    if (salaryPreset !== 'custom' && (value !== boundary.startTime || salaryEndTime !== boundary.endTime)) {
      keepSalaryCustomRange();
    }
  };

  const handleSalaryEndTimeChange = (value) => {
    setSalaryEndTime(value);
    const boundary = getBusinessBoundaryTimes(businessDayStartHour);
    if (salaryPreset !== 'custom' && (salaryStartTime !== boundary.startTime || value !== boundary.endTime)) {
      keepSalaryCustomRange();
    }
  };

  const salaryPeriodRange = useMemo(() => {
    if (salaryView === 'calendar') {
      if (salaryPreset === 'year') {
        return {
          from: `${salaryCalMonth.y}-01-01`,
          to: `${salaryCalMonth.y}-12-31`,
        };
      }
      const monthStart = `${salaryCalMonth.y}-${String(salaryCalMonth.m).padStart(2, '0')}-01`;
      const monthEnd = `${salaryCalMonth.y}-${String(salaryCalMonth.m).padStart(2, '0')}-${String(new Date(salaryCalMonth.y, salaryCalMonth.m, 0).getDate()).padStart(2, '0')}`;
      return { from: monthStart, to: monthEnd };
    }
    return salaryFilterRange;
  }, [salaryView, salaryPreset, salaryCalMonth, salaryFilterRange]);

  const salaryCycleMonthReference = getSalaryCycleMonthReference(
    salaryPeriodRange.from,
    salaryPeriodRange.to,
  );
  const effectiveSalaryAsOfDate = salaryCycleMonthReference
    ? currentBusinessDate
    : (salaryPeriodRange.to && salaryPeriodRange.to < currentBusinessDate
      ? salaryPeriodRange.to
      : currentBusinessDate);
  const getSalarySummaryForCalendarDate = (staffMember, targetDate) => (
    getSalarySummaryForDisplayDate(staffMember, advances, targetDate, effectiveSalaryAsOfDate)
  );

  useEffect(() => {
    let cancelled = false;
    const fetchSalaryOverview = async () => {
      if (!salaryPeriodRange.from || !salaryPeriodRange.to) {
        setSalaryOverview({ summary: {}, staff_summary: [], event_rows: [] });
        return;
      }
      setSalaryOverviewLoading(true);
      try {
        const res = await api.get(
          '/staff-payroll/overview/',
          buildSalaryOverviewParams(
            salaryPeriodRange.from,
            salaryPeriodRange.to,
            effectiveSalaryAsOfDate,
            salaryCycleMonthReference ? { cycle_month: salaryCycleMonthReference } : {},
          ),
        );
        if (!cancelled) {
          setSalaryOverview(res.data || { summary: {}, staff_summary: [], event_rows: [] });
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(formatApiError(error, 'Failed to load salary overview'));
          setSalaryOverview({ summary: {}, staff_summary: [], event_rows: [] });
        }
      } finally {
        if (!cancelled) {
          setSalaryOverviewLoading(false);
        }
      }
    };
    if (activeTab === 'salary_schedule') {
      fetchSalaryOverview();
    }
    return () => { cancelled = true; };
  }, [activeTab, salaryPeriodRange.from, salaryPeriodRange.to, effectiveSalaryAsOfDate, salaryCycleMonthReference, advances]);

  const salaryOverviewEventRows = useMemo(
    () => (activeTab === 'salary_schedule' ? (salaryOverview?.event_rows || []) : []),
    [activeTab, salaryOverview]
  );
  const salaryOverviewRows = useMemo(
    () => (
      activeTab === 'salary_schedule'
        ? alignSalarySummaryRowsWithEvents(salaryOverview?.staff_summary || [], salaryOverviewEventRows)
        : []
    ),
    [activeTab, salaryOverview, salaryOverviewEventRows]
  );

  // Salary filter — only staff who were in service during the selected period (time-bound), so a
  // staff terminated before the period never leaks in (e.g. Ammad in July).
  const salaryStaffOptions = useMemo(
    () => buildStaffDropdownOptions(staff, { fromDate: salaryPeriodRange.from, toDate: salaryPeriodRange.to, keepValue: staffFilter }),
    [staff, salaryPeriodRange.from, salaryPeriodRange.to, staffFilter]
  );

  useEffect(() => {
    if (activeTab !== 'salary_schedule' || !staffFilter) return;
    if (!salaryStaffOptions.some((option) => String(option.value) === String(staffFilter))) {
      setStaffFilter('');
    }
  }, [activeTab, salaryStaffOptions, staffFilter]);

  // Surface employment / re-employment / termination events as (zero-amount) rows in the salary
  // schedule, scoped to the same period as the backend's event rows.
  const salaryEventRowsWithLifecycle = useMemo(() => {
    if (activeTab !== 'salary_schedule') return salaryOverviewEventRows;
    const from = salaryPeriodRange.from, to = salaryPeriodRange.to;
    const staffNameById = {};
    staff.forEach(s => { staffNameById[String(s.id)] = s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim(); });
    const lifeRows = (lifecycleEventsList || [])
      .filter(e => LIFECYCLE_MARKER_TYPES.includes(e.event_type))
      .map(e => ({ ...e, _d: String(e.event_date || '').substring(0, 10) }))
      .filter(e => e._d && (!from || e._d >= from) && (!to || e._d <= to))
      .map(e => ({
        id: `lifecycle-${e.id}`, rowType: 'lifecycle', type: 'lifecycle', lifecycleType: e.event_type,
        date: e._d, staffId: e.staff, staffName: e.staff_name || staffNameById[String(e.staff)] || '',
        department: '', amount: 0, status: e.event_type_label || e.event_type, note: e.reason || e.notes || '',
        remainingDue: 0, remainingSigned: 0,
      }));
    return [...salaryOverviewEventRows, ...lifeRows];
  }, [activeTab, salaryOverviewEventRows, lifecycleEventsList, salaryPeriodRange.from, salaryPeriodRange.to, staff]);

  const filteredSalaryEvents = useMemo(
    () => filterSalaryEventRows(salaryEventRowsWithLifecycle, {
      staffFilter,
      search: salarySearch,
      entryFilter: salaryEntryFilter,
    }),
    [salaryEventRowsWithLifecycle, staffFilter, salarySearch, salaryEntryFilter]
  );
  const sortedSalaryEvents = useMemo(
    () => salarySortData(filteredSalaryEvents, (row, key) => {
      switch (key) {
        case 'staff_name':
          return row.staffName;
        case 'row_type':
          return row.rowType;
        case 'remaining_due':
          return row.remainingDue;
        case 'note':
          return row.note;
        default:
          return row[key];
      }
    }),
    [filteredSalaryEvents, salarySortData]
  );

  const salaryAggregateRows = useMemo(
    () => filterSalarySummaryRows(salaryOverviewRows, {
      staffFilter,
      search: salarySearch,
    }),
    [salaryOverviewRows, staffFilter, salarySearch]
  );

  const salaryAggregateGrandTotal = useMemo(
    () => buildSalaryGrandTotal(salaryAggregateRows),
    [salaryAggregateRows]
  );

  const filteredSalarySchedule = useMemo(() => [], []);

  const salaryAggregateIdsKey = useMemo(
    () => salaryAggregateRows.map((row) => String(row.staffId)).join('|'),
    [salaryAggregateRows]
  );

  useEffect(() => {
    setCollapsedSalarySummaryStaff(new Set(salaryAggregateRows.map((row) => row.staffId)));
  }, [salaryAggregateIdsKey, salaryPeriodRange.from, salaryPeriodRange.to, staffFilter, salarySearch]);

  const toggleSalarySummaryStaff = (staffId) => {
    setCollapsedSalarySummaryStaff((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  };

  const getSalaryEntryBadgeClass = (rowType) => {
    if (rowType === 'salary') return 'bg-green-100 text-green-700';
    if (['advance', 'bonus', 'gift'].includes(rowType)) return 'bg-green-100 text-green-700';
    if (rowType === 'deduction') return 'bg-rose-100 text-rose-700';
    if (rowType === 'lifecycle') return 'bg-emerald-100 text-emerald-700';
    return 'bg-primary-100 text-primary-700';
  };

  const getSalaryEntryLabel = (rowType, row) => {
    if (rowType === 'salary') return 'Salary';
    if (rowType === 'advance') return 'Advance';
    if (rowType === 'bonus') return 'Bonus';
    if (rowType === 'gift') return 'Gift';
    if (rowType === 'deduction') return 'Deduction';
    if (rowType === 'lifecycle') return ({ employed: 'Employed', reemployed: 'Re-employed', terminated: 'Terminated' }[row?.lifecycleType] || 'Event');
    return 'Due';
  };

  const salaryCalData = useMemo(() => {
    const calendarRows = filterSalaryEventRows(salaryOverviewEventRows, {
      staffFilter,
      search: salarySearch,
      entryFilter: 'all',
    });
    return buildSalaryCalendarData(calendarRows);
  }, [salaryOverviewEventRows, staffFilter, salarySearch]);

  const renderSalaryCalendarLines = (entries, toneClass, marker, limit = 2, showDue = false) => {
    if (!entries?.length) return null;
    return (
      <>
        {entries.slice(0, limit).map((entry, index) => (
          <div key={`${marker}-${entry.staffId || entry.staffName}-${index}`} className={`text-[9px] font-semibold leading-tight truncate ${toneClass}`}>
            {marker} {getShortStaffName(entry.staffName)} {formatCompactK(entry.amount)}
            {showDue && entry.remainingSigned != null && (
              Math.abs(entry.remainingSigned) < 0.01
                ? <span className="text-gray-500"> (0?)</span>
                : <span className={entry.remainingSigned < 0 ? 'text-emerald-600' : 'text-primary-600'}> (?{formatCompactK(entry.remainingSigned)})</span>
            )}
          </div>
        ))}
        {entries.length > limit && (
          <div className={`text-[9px] leading-tight ${toneClass}`}>+{entries.length - limit} more</div>
        )}
      </>
    );
  };

  const rosterMap = useMemo(() => {
    const map = {};
    rosters.forEach(r => { map[`${r.staff}_${r.roster_date}`] = r.shift_start; });
    return map;
  }, [rosters]);

  const firstPunchInSet = useMemo(() => {
    const set = new Set();
    const seen = {};
    const sorted = [...attendance].sort((a, b) => new Date(a.punch_time) - new Date(b.punch_time));
    sorted.forEach(item => {
      if (item.punch_type !== 'in' || !item.punch_time) return;
      const d = getBusinessDateForDateTimeValue(item.punch_time, businessDayStartHour);
      const key = `${item.staff}_${d}`;
      if (!seen[key]) { seen[key] = true; set.add(item.id); }
    });
    return set;
  }, [attendance, businessDayStartHour]);

  const leaveMap = useMemo(() => {
    const map = {};
    leaveEntries.forEach(l => {
      if (!l.start_date) return;
      const start = new Date(l.start_date); const end = new Date(l.end_date || l.start_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        map[`${l.staff}_${d.toISOString().split('T')[0]}`] = l;
      }
    });
    return map;
  }, [leaveEntries]);

  // Off-day (leave/holiday/absentee) lookup keyed by `${staff}_${YYYY-MM-DD}` — used to flag
  // punches that land on a day the staff was recorded as off (a mismatch).
  const offDayMap = useMemo(() => {
    const map = {};
    offDayRecords.forEach(r => {
      if (!r.date) return;
      map[`${r.staff}_${String(r.date).substring(0, 10)}`] = r;
    });
    return map;
  }, [offDayRecords]);

  // Employment / re-employment / termination events keyed by `${staff}_${YYYY-MM-DD}` —
  // surfaced as markers/rows on the Attendance and Salary views.
  const lifecycleMap = useMemo(() => {
    const m = {};
    (lifecycleEventsList || []).forEach(e => {
      if (!LIFECYCLE_MARKER_TYPES.includes(e.event_type)) return;
      const d = String(e.event_date || '').substring(0, 10);
      if (!d) return;
      (m[`${e.staff}_${d}`] = m[`${e.staff}_${d}`] || []).push(e);
    });
    return m;
  }, [lifecycleEventsList]);

  const getEffectiveShopHours = (targetDate, dayOfWeek) =>
    [...shopHours]
      .filter(h => h.day_of_week === dayOfWeek && h.effective_from <= targetDate)
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] || null;

  const getDayOfWeek = (dateStr) => { const d = new Date(dateStr); return (d.getDay() + 6) % 7; };
  // A staff member's schedule for a date: their roster if set, else the shop business hours.
  const getStaffScheduleLabel = (staffId, targetDate) => {
    if (!staffId || !targetDate) return '';
    const roster = rosters.find(r => String(r.staff) === String(staffId) && r.roster_date === targetDate);
    if (roster && roster.shift_start) {
      return `Roster ${String(roster.shift_start).slice(0, 5)}${roster.shift_end ? '–' + String(roster.shift_end).slice(0, 5) : ''}`;
    }
    const sh = getEffectiveShopHours(targetDate, getDayOfWeek(targetDate));
    if (sh && sh.open_time) {
      return `Shop ${String(sh.open_time).slice(0, 5)}${sh.close_time ? '–' + String(sh.close_time).slice(0, 5) : ''}`;
    }
    return 'No schedule';
  };
  // Shop business hours label for a date, e.g. "Shop hours 11:00–23:00" (for attendance popup + day-group header).
  const getShopHoursLabel = (targetDate) => {
    if (!targetDate) return '';
    const sh = getEffectiveShopHours(targetDate, getDayOfWeek(targetDate));
    if (sh && sh.open_time) {
      return `Shop hours ${String(sh.open_time).slice(0, 5)}${sh.close_time ? '–' + String(sh.close_time).slice(0, 5) : ''}`;
    }
    return 'Shop closed';
  };
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [wageTypeHistory, setWageTypeHistory] = useState([]);
  const [selectedPunches, setSelectedPunches] = useState(() => new Set());
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventForm, setEventForm] = useState(initialStaffEventForm);
  const [pendingStaffEventEntries, setPendingStaffEventEntries] = useState([]);
  const [selectedStaffEventsStaff, setSelectedStaffEventsStaff] = useState(null);
  const [selectedStaffEvents, setSelectedStaffEvents] = useState([]);
  const [staffEventsLoading, setStaffEventsLoading] = useState(false);
  const [staffForm, setStaffForm] = useState(initialStaff);
  const [attendanceForm, setAttendanceForm] = useState(initialAttendance);
  const attendanceFormDateTime = useMemo(
    () => splitDateTimeInputValue(attendanceForm.punch_time, currentBusinessDate),
    [attendanceForm.punch_time, currentBusinessDate]
  );
  const [attendanceReason, setAttendanceReason] = useState('');
  const [leaveForm, setLeaveForm] = useState(initialLeave);
  const [rosterForm, setRosterForm] = useState(initialRoster);
  const [advanceForm, setAdvanceForm] = useState(initialAdvance);
  const [shopHoursForm, setShopHoursForm] = useState(initialShopHours);
  const [showSmartAttModal, setShowSmartAttModal] = useState(false);
  const [smartAttText, setSmartAttText] = useState('');
  const [smartAttStep, setSmartAttStep] = useState('input');
  const [smartAttRows, setSmartAttRows] = useState([]);
  const [smartAttDate, setSmartAttDate] = useState('');
  const [smartAttSaving, setSmartAttSaving] = useState(false);
  const smartAttModalRef = useRef(null);
  const [smartAttModalSize, setSmartAttModalSize] = useState({ width: null, height: null });
  const [showAllStaff, setShowAllStaff] = useState(false);

  useEffect(() => {
    if (!restrictedTodayOnly) return;
    if (activeTab === 'attendance' && attView !== 'table') setAttView('table');
    if (activeTab === 'leave' && leaveView !== 'table') setLeaveView('table');
    if (showAllStaff) setShowAllStaff(false);
    if (datePreset !== 'today' || startDate !== currentBusinessDate || endDate !== currentBusinessDate) {
      applyPreset('today');
    }
  }, [restrictedTodayOnly, activeTab, attView, leaveView, showAllStaff, datePreset, startDate, endDate, currentBusinessDate, applyPreset]);

  // Load the pay-type (wage_type) change history for the staff being edited.
  useEffect(() => {
    if (!showModal || activeTab !== 'staff' || !editingItem?.id) { setWageTypeHistory([]); return; }
    api.get(`/staff-lifecycle-events/?staff_id=${editingItem.id}&event_type=wage_type_change&page_size=200`)
      .then((res) => {
        const changes = (res.data.results || res.data || [])
          .slice()
          .sort((a, b) => String(a.event_date).localeCompare(String(b.event_date)));
        const periods = [];
        let curType = changes.length ? (changes[0].previous_wage_type || editingItem.wage_type) : editingItem.wage_type;
        let curFrom = editingItem.hire_date;
        for (const ch of changes) {
          periods.push({ from: curFrom, to: ch.event_date, type: curType });
          curType = ch.wage_type;
          curFrom = ch.event_date;
        }
        periods.push({ from: curFrom, to: null, type: curType });
        setWageTypeHistory(periods);
      })
      .catch(() => setWageTypeHistory([]));
  }, [showModal, activeTab, editingItem?.id]);

  useEffect(() => {
    setLeaveQuick(prev => ({ ...prev, date: currentBusinessDate }));
    if (restrictedTodayOnly && activeTab === 'leave' && !editingItem) {
      setLeaveForm(prev => ({ ...prev, start_date: currentBusinessDate, end_date: currentBusinessDate }));
    }
  }, [currentBusinessDate, restrictedTodayOnly, activeTab, editingItem]);

  useEffect(() => {
    if (activeTab === 'deductions' && datePreset === 'today') {
      applyPreset('year');
    }
  }, [activeTab, datePreset, applyPreset]);
  const startAttResize = (e) => {
    e.preventDefault();
    const startX = e.clientX; const startY = e.clientY;
    const startW = smartAttModalRef.current?.offsetWidth || 700;
    const startH = smartAttModalRef.current?.offsetHeight || 600;
    const onMove = (ev) => setSmartAttModalSize({ width: Math.max(500, startW + ev.clientX - startX), height: Math.max(300, startH + ev.clientY - startY) });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    api.get('/settings/').then(res => setBusinessDayStartHour(res.data?.business_day_start_hour ?? 6)).catch(() => {});
    if (adminUser) {
      api.get('/users/?page_size=500').then(res => setUsers(res.data.results || res.data)).catch(() => {});
    }
    fetchAll();
  }, []);

  // Refetch attendance scoped to the visible date range so historical periods load fully
  // (the initial fetchAll only pulls the most recent 2000 punches, which hides older entries).
  const loadAttendanceRange = async () => {
    const s = startDate;
    const e = filterEndCalendarDate || endDate;
    if (!s || !e) return;
    try {
      const res = await api.get(`/staff-attendance/?page_size=20000&start_date=${s}&end_date=${e}`);
      setAttendance(res.data.results || res.data);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    loadAttendanceRange();
  }, [startDate, endDate, filterEndCalendarDate]);

  const fetchAll = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [deptRes, staffData, attendanceRes, leaveRes, rosterRes, advancesData, shopHoursRes, holidayRes, lifecycleEvents, perfRes] = await Promise.all([
        api.get('/departments/?page_size=500').catch(() => ({ data: [] })),
        fetchPaginatedCollection(api, '/staff/', { pageSize: 500 }),
        api.get('/staff-attendance/?page_size=2000').catch(() => ({ data: [] })),
        api.get('/staff-leaves/?page_size=2000').catch(() => ({ data: [] })),
        api.get('/staff-rosters/?page_size=2000').catch(() => ({ data: [] })),
        fetchPaginatedCollection(api, '/staff-advances/', { pageSize: 500, maxRecords: 50000 }).catch(() => []),
        api.get('/shop-hours/?page_size=500').catch(() => ({ data: [] })),
        api.get('/public-holidays/?page_size=500').catch(() => ({ data: [] })),
        fetchPaginatedCollection(api, '/staff-lifecycle-events/', { pageSize: 500, maxRecords: 10000 }).catch(() => []),
        api.get('/staff-performance/?page_size=2000').catch(() => ({ data: [] })),
      ]);
      setDepartments(deptRes.data.results || deptRes.data);
      const staffRows = Array.isArray(staffData) ? staffData : (staffData?.results || staffData || []);
      let mergedStaff = staffRows;
      try {
        mergedStaff = mergeStaffWithLifecycleHistory(staffRows, lifecycleEvents);
      } catch (mergeError) {
        console.error('Failed to merge staff lifecycle history', mergeError);
        toast.error('Staff history could not be fully merged. Showing base staff records.');
      }
      setStaff(mergedStaff);
      setLifecycleEventsList(Array.isArray(lifecycleEvents) ? lifecycleEvents : (lifecycleEvents?.results || []));
      if (selectedStaffEventsStaff?.id) {
        const refreshedStaff = mergedStaff.find((member) => String(member.id) === String(selectedStaffEventsStaff.id));
        if (refreshedStaff) {
          setSelectedStaffEventsStaff(refreshedStaff);
        }
      }
      setAttendance(attendanceRes.data.results || attendanceRes.data);
      setLeaveEntries(leaveRes.data.results || leaveRes.data);
      const perfRows = perfRes.data.results || perfRes.data || [];
      setOffDayRecords(Array.isArray(perfRows) ? perfRows.filter(p => ['leave', 'holiday', 'absentee'].includes(p.violation_type)) : []);
      setRosters(rosterRes.data.results || rosterRes.data);
      setAdvances(Array.isArray(advancesData) ? advancesData : (advancesData?.results || advancesData || []));
      setShopHours(shopHoursRes.data.results || shopHoursRes.data);
      setPublicHolidays(holidayRes.data.results || holidayRes.data);
      // Override the recent-2000 punches with the full visible date range so that the
      // attendance list, missing-entry placeholders and calendar counts reflect the
      // current view immediately after an add/edit/delete (not only after a page refresh).
      await loadAttendanceRange();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load staff management data'));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const normalizeStaffEvents = (financialEvents, lifecycleEvents) => {
    const financial = (financialEvents || []).map((item) => ({
      key: `financial-${item.id}`,
      source: 'financial',
      id: item.id,
      staff: item.staff,
      event_type: item.payment_type,
      event_type_label: item.payment_type_label || item.payment_type,
      event_date: item.advance_date,
      amount: parseFloat(item.amount || 0),
      reason: item.reason || '',
      status: item.status || '',
      created_by_name: item.created_by_name || '',
      summary: '',
    }));
    const lifecycle = (lifecycleEvents || []).map((item) => ({
      key: `lifecycle-${item.id}`,
      source: 'lifecycle',
      id: item.id,
      staff: item.staff,
      event_type: item.event_type,
      event_type_label: item.event_type_label || item.event_type,
      event_date: item.event_date,
      amount: null,
      reason: item.reason || '',
      status: '',
      created_by_name: item.created_by_name || '',
      department: item.department || '',
      department_name: item.department_name || '',
      monthly_salary: item.monthly_salary == null ? '' : String(item.monthly_salary),
      salary_date: item.salary_date == null ? '' : String(item.salary_date),
      employment_status_value: item.employment_status_value || '',
      employment_status_value_label: item.employment_status_value_label || '',
      summary:
        item.event_type === 'department_change'
          ? (item.department_name || 'No Department')
          : item.event_type === 'salary_change'
            ? formatSalaryCurrency(item.monthly_salary || 0)
            : item.event_type === 'salary_date_change'
              ? `Day ${item.salary_date || '—'}`
              : item.event_type === 'status_change'
                ? (item.employment_status_value_label || item.employment_status_value || '')
                : item.reason || '',
    }));
    return [...financial, ...lifecycle].sort((a, b) => String(b.event_date || '').localeCompare(String(a.event_date || '')) || String(b.id || '').localeCompare(String(a.id || '')));
  };

  const loadStaffEvents = async (staffMember) => {
    if (!staffMember?.id) return;
    setSelectedStaffEventsStaff(staffMember);
    setStaffEventsLoading(true);
    try {
      const [financialEvents, lifecycleEvents] = await Promise.all([
        fetchPaginatedCollection(api, '/staff-advances/', { pageSize: 500, maxRecords: 50000, params: { staff_id: staffMember.id } }),
        fetchPaginatedCollection(api, '/staff-lifecycle-events/', { pageSize: 500, maxRecords: 10000, params: { staff_id: staffMember.id } }),
      ]);
      setSelectedStaffEvents(normalizeStaffEvents(financialEvents, lifecycleEvents));
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load staff events'));
    } finally {
      setStaffEventsLoading(false);
    }
  };

  const resetForms = () => {
    setEditingItem(null);
    setEventForm(initialStaffEventForm);
    setStaffForm(initialStaff);
    setAttendanceForm({ ...initialAttendance, punch_time: getLocalDateTimeInputValue() });
    setAttendanceReason('');
    setLeaveForm({ ...initialLeave, start_date: currentBusinessDate, end_date: currentBusinessDate });
    setLeaveEntryType('leave');
    { const sh = getEffectiveShopHours(currentBusinessDate, getDayOfWeek(currentBusinessDate));
      setRosterForm({ ...initialRoster, roster_date: currentBusinessDate,
        shift_start: sh ? String(sh.open_time).slice(0, 5) : initialRoster.shift_start,
        shift_end: sh ? String(sh.close_time).slice(0, 5) : initialRoster.shift_end }); }
    setAdvanceForm(initialAdvance);
    setShopHoursForm(initialShopHours);
    setEditingShopHoursGroup(null);
    setEditingHoliday(null);
    setHolidayForm(initialPublicHoliday);
  };

  // Attendance record-type filter (dropdown after the staff filter): punches, leave, holiday,
  // absentee, or employment/termination events.
  const matchesAttRecordFilter = (row) => {
    if (attRecordFilter === 'all') return true;
    if (attRecordFilter === 'punch') return row._entryType === 'punch' || (row._placeholder && !row._onLeave);
    if (attRecordFilter === 'lifecycle') return !!row._lifecycle;
    if (attRecordFilter === 'leave') return (row._offDay && row.violation_type === 'leave') || row._onLeave;
    if (attRecordFilter === 'holiday') return row._offDay && row.violation_type === 'holiday';
    if (attRecordFilter === 'absentee') return row._offDay && row.violation_type === 'absentee';
    return true;
  };

  const openCreateModal = () => {
    resetForms();
    if (activeTab === 'deductions') {
      setAdvanceForm({ ...initialAdvance, payment_type: 'deduction' });
    }
    if (activeTab === 'attendance') {
      setAttendanceForm({ ...initialAttendance, punch_time: getLocalDateTimeInputValue() });
    }
    if (restrictedTodayOnly && activeTab === 'leave') {
      setLeaveForm({ ...initialLeave, start_date: currentBusinessDate, end_date: currentBusinessDate });
      setLeaveEntryType('leave');
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setPendingAttendanceEntries([]);
    setPendingLeaveEntries([]);
    resetForms();
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setEventForm(initialStaffEventForm);
    setPendingStaffEventEntries([]);
  };

  const { sortKey: smSortKey, sortDir: smSortDir, handleSort: smHandleSort, sortData: smSortData } = useSortable('', 'asc');

  const currentItems = useMemo(() => computeCurrentItems({
    activeTab, staff, attendance, leaveEntries, offDayRecords, lifecycleEventsList,
    rosters, advances, shopHours, shopHoursListTab, publicHolidays, search,
    startDate, endDate, filterEndCalendarDate, startTime, endTime, staffFilter,
    smSortKey, smSortData, businessDayStartHour,
    getBusinessDateForDateTimeValue, matchesAttRecordFilter,
  }), [activeTab, staff, attendance, leaveEntries, offDayRecords, lifecycleEventsList, rosters, advances, shopHours, shopHoursListTab, publicHolidays, search, startDate, endDate, filterEndCalendarDate, startTime, endTime, staffFilter, attRecordFilter, smSortKey, smSortDir, businessDayStartHour]);

  const groupedShopHours = useMemo(() => {
    const byDate = {};
    shopHours.forEach(sh => {
      const d = sh.effective_from;
      if (!byDate[d]) byDate[d] = [];
      byDate[d].push(sh);
    });
    return Object.entries(byDate)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, entries]) => {
        const byTimes = {};
        entries.forEach(e => {
          const key = `${e.open_time}__${e.close_time}`;
          if (!byTimes[key]) byTimes[key] = { open_time: e.open_time, close_time: e.close_time, dayEntries: [], notes: e.notes };
          byTimes[key].dayEntries.push({ day: Number(e.day_of_week), id: e.id });
        });
        return {
          effective_from: date,
          groups: Object.values(byTimes).map(g => ({
            ...g,
            dayEntries: [...g.dayEntries].sort((a, b) => a.day - b.day),
            days: [...g.dayEntries].map(entry => entry.day).sort((a, b) => a - b),
            ids: [...g.dayEntries].map(entry => entry.id),
          }))
        };
      });
  }, [shopHours]);

  const shopHoursDateRange = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const current = new Date(`${today}T12:00:00`);
    const toIso = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (shPreset === 'today') return { s: today, e: today };
    if (shPreset === 'week') { const d = new Date(); const mon = new Date(d); mon.setDate(d.getDate() - (d.getDay() + 6) % 7); const sun = new Date(mon); sun.setDate(mon.getDate() + 6); return { s: mon.toISOString().split('T')[0], e: sun.toISOString().split('T')[0] }; }
    if (shPreset === 'month') return { s: toIso(new Date(current.getFullYear(), current.getMonth(), 1)), e: toIso(new Date(current.getFullYear(), current.getMonth() + 1, 0)) };
    if (shPreset === 'year') return { s: `${current.getFullYear()}-01-01`, e: `${current.getFullYear()}-12-31` };
    if (shPreset === 'custom') return { s: shCustomStart, e: shCustomEnd };
    return { s: '', e: '' };
  }, [shPreset, shCustomStart, shCustomEnd]);

  const filteredGroupedShopHours = useMemo(() => {
    const { s, e } = shopHoursDateRange;
    if (!s && !e) return groupedShopHours;
    return groupedShopHours.filter(({ effective_from: d }) => (!s || d >= s) && (!e || d <= e));
  }, [groupedShopHours, shopHoursDateRange]);

  const holidayMap = useMemo(() => {
    const map = {};
    publicHolidays.forEach((holiday) => {
      const start = holiday.start_date;
      const end = holiday.end_date || holiday.start_date;
      if (!start) return;
      let cursor = new Date(`${start}T12:00:00`);
      const endDate = new Date(`${end}T12:00:00`);
      while (!Number.isNaN(cursor.getTime()) && cursor <= endDate) {
        map[isoDate(cursor)] = holiday;
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [publicHolidays]);

  const getHolidayForDate = (dateStr) => holidayMap[dateStr] || null;
  const isHolidayDate = (dateStr) => !!getHolidayForDate(dateStr);

  const filteredPublicHolidays = useMemo(() => {
    const { s, e } = shopHoursDateRange;
    if (!s && !e) return publicHolidays;
    return publicHolidays.filter((holiday) => {
      const holidayEnd = holiday.end_date || holiday.start_date;
      return (!s || holidayEnd >= s) && (!e || holiday.start_date <= e);
    });
  }, [publicHolidays, shopHoursDateRange]);

  const displayItems = useMemo(() => computeDisplayItems({
    activeTab, showAllStaff, currentItems, staff, staffFilter,
    startDate, endDate, leaveMap, offDayMap, lifecycleMap, businessDayStartHour,
    getBusinessDateForDateTimeValue, isStaffEligibleOnDate, matchesAttRecordFilter,
  }), [activeTab, showAllStaff, currentItems, staff, staffFilter, attRecordFilter, startDate, endDate, leaveMap, offDayMap, lifecycleMap, businessDayStartHour]);

  const calendarData = useMemo(() => computeCalendarData({
    activeTab, calMonthYM, attendance, leaveEntries, rosters, advances,
    leaveMap, lifecycleMap, offDayMap, staff, staffFilter, businessDayStartHour,
    getBusinessDateForDateTimeValue, isStaffEligibleOnDate, getSalarySummaryForCalendarDate,
  }), [activeTab, calMonthYM, attendance, leaveEntries, rosters, advances, leaveMap, lifecycleMap, offDayMap, staff, staffFilter, businessDayStartHour]);

  // Sort accessor for the attendance table's computed columns (Status, Reason) which aren't plain
  // fields. Other keys fall through to the item's own property.
  const attnSortAccessor = (item, key) => {
    if (key === 'reason') {
      if (item._offDay) return item.reason || '';
      if (item._onLeave) return item._leaveData?.leave_type || '';
      if (item.punch_time && !item._placeholder) return String(item.notes || '').split(' - ')[0].trim();
      return '';
    }
    if (key === 'status') {
      if (item._placeholder) return item.punch_type === 'in' ? 'zz-missing-in' : 'zz-missing-out';
      if (item._offDay) return item.is_warning ? 'warning' : 'deduct';
      if (item._onLeave) return 'leave';
      if (item.punch_time) {
        const pd = getBusinessDateForDateTimeValue(item.punch_time, businessDayStartHour);
        const ss = pd ? rosterMap[`${item.staff}_${pd}`] : null;
        return ((item.punch_type === 'in' && firstPunchInSet.has(item.id)) ? getArrivalStatus(item.punch_time, ss) : '') || '';
      }
      return '';
    }
    return item[key];
  };

  const groupedByDate = useMemo(() => computeGroupedByDate({
    activeTab, displayItems, businessDayStartHour,
    getBusinessDateForDateTimeValue, smSortData, attnSortAccessor,
  }), [activeTab, displayItems, smSortKey, smSortDir, businessDayStartHour]);

  const allDates = useMemo(() => groupedByDate ? groupedByDate.map(([d]) => d) : [], [groupedByDate]);

  const toggleDate = (date) => {
    setCollapsedDates(prev => { const n = new Set(prev); if (n.has(date)) n.delete(date); else n.add(date); return n; });
  };

  const changeCalMonth = (delta) => {
    const [yr, mo] = calMonthYM.split('-').map(Number);
    const d = new Date(yr, mo - 1 + delta, 1);
    const newYm = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    const firstDay = `${newYm}-01`;
    const lastDate = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const lastDay = `${newYm}-${String(lastDate).padStart(2,'0')}`;
    setCalMonthYM(newYm);
    setStartDate(firstDay);
    setEndDate(lastDay);
    setDatePreset('month');
  };

  const drillToMonth = (ym) => {
    const [yr, mo] = ym.split('-').map(Number);
    const firstDay = `${yr}-${String(mo).padStart(2,'0')}-01`;
    const lastDate = new Date(yr, mo, 0).getDate();
    const lastDay = `${yr}-${String(mo).padStart(2,'0')}-${String(lastDate).padStart(2,'0')}`;
    setStartDate(firstDay); setEndDate(lastDay); setDatePreset('month'); setCalMonthYM(ym);
  };

  const yearCalData = useMemo(() => computeYearCalData({
    datePreset, activeTab, startDate, attendance, leaveEntries, rosters, advances,
    staffFilter, businessDayStartHour, getBusinessDateForDateTimeValue,
  }), [datePreset, activeTab, startDate, attendance, leaveEntries, rosters, advances, staffFilter, businessDayStartHour]);

  const handleApplyShopHours = async () => {
    const { effective_from, open_time, close_time, notes, selectedDays } = shopHoursForm;
    if (!effective_from || !open_time || !close_time) { toast.error('Please fill date and times'); return; }
    if (selectedDays.length === 0) { toast.error('Select at least one day'); return; }
    setShopHoursSaving(true);
    try {
      await Promise.all(selectedDays.map(day =>
        api.post('/shop-hours/', { day_of_week: day, effective_from, open_time, close_time, notes })
      ));
      toast.success(`${selectedDays.length} day(s) saved`);
      fetchAll();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save shop hours'));
    } finally {
      setShopHoursSaving(false);
    }
  };

  const handleApplyShopHoursPending = () => {
    const { effective_from, open_time, close_time, notes, selectedDays } = shopHoursForm;
    if (!effective_from || !open_time || !close_time) { toast.error('Please fill date and times'); return; }
    if (selectedDays.length === 0) { toast.error('Select at least one day'); return; }
    setPendingShopHoursEntries(prev => [...prev, { _key: Date.now(), effective_from, open_time, close_time, notes, days: [...selectedDays] }]);
  };

  const handleSaveAllShopHours = async () => {
    if (editingShopHoursGroup) {
      const { effective_from, open_time, close_time, notes, selectedDays } = shopHoursForm;
      if (!effective_from || !open_time || !close_time) { toast.error('Please fill date and times'); return; }
      if (selectedDays.length === 0) { toast.error('Select at least one day'); return; }
      setShopHoursSaving(true);
      try {
        const currentIdsByDay = new Map((editingShopHoursGroup.dayEntries || []).map(entry => [entry.day, entry.id]));
        const updatePromises = selectedDays.map((day) => {
          const existingId = currentIdsByDay.get(day);
          const payload = { day_of_week: day, effective_from, open_time, close_time, notes };
          return existingId
            ? api.patch(`/shop-hours/${existingId}/`, payload)
            : api.post('/shop-hours/', payload);
        });
        const deleteIds = (editingShopHoursGroup.dayEntries || [])
          .filter(entry => !selectedDays.includes(entry.day))
          .map(entry => entry.id);
        await Promise.all([...updatePromises, ...deleteIds.map(id => api.delete(`/shop-hours/${id}/`))]);
        toast.success('Shop hours updated');
        setShowShopHoursModal(false);
        setEditingShopHoursGroup(null);
        setPendingShopHoursEntries([]);
        setShopHoursForm(initialShopHours);
        fetchAll();
      } catch (error) {
        toast.error(formatApiError(error, 'Failed to update shop hours'));
      } finally {
        setShopHoursSaving(false);
      }
      return;
    }
    if (pendingShopHoursEntries.length === 0) { toast.error('Nothing to save'); return; }
    setShopHoursSaving(true);
    try {
      await Promise.all(pendingShopHoursEntries.flatMap(e =>
        e.days.map(day => api.post('/shop-hours/', { day_of_week: day, effective_from: e.effective_from, open_time: e.open_time, close_time: e.close_time, notes: e.notes }))
      ));
      const total = pendingShopHoursEntries.reduce((s, e) => s + e.days.length, 0);
      toast.success(`${total} shop hour entry(ies) saved`);
      setShowShopHoursModal(false);
      setPendingShopHoursEntries([]);
      fetchAll();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save shop hours'));
    } finally { setShopHoursSaving(false); }
  };

  const handleDeleteShopHoursGroup = async (ids, label) => {
    if (!window.confirm(`Delete shop hours for ${label}?`)) return;
    const idSet = new Set(ids.map(String));
    const previous = shopHours;
    // Optimistically remove the rows so the list updates instantly and stays in sync.
    setShopHours(prev => prev.filter(sh => !idSet.has(String(sh.id))));
    try {
      await Promise.all(ids.map(id => api.delete(`/shop-hours/${id}/`)));
      toast.success('Deleted');
      fetchAll();
    } catch (error) {
      setShopHours(previous); // rollback on failure
      toast.error(formatApiError(error, 'Failed to delete'));
    }
  };

  const openEditShopHoursGroup = (effectiveFrom, group) => {
    setEditingShopHoursGroup({ ...group, effective_from: effectiveFrom });
    setEditingHoliday(null);
    setHolidayForm(initialPublicHoliday);
    setPendingShopHoursEntries([]);
    setShopHoursModalTab('hours');
    setShopHoursForm({
      effective_from: effectiveFrom,
      open_time: group.open_time,
      close_time: group.close_time,
      notes: group.notes || '',
      selectedDays: [...(group.days || [])],
    });
    setShowShopHoursModal(true);
  };

  const savePublicHoliday = async () => {
    if (!holidayForm.name.trim() || !holidayForm.start_date) {
      toast.error('Please provide a holiday name and start date');
      return;
    }
    setHolidaySaving(true);
    try {
      const payload = {
        name: holidayForm.name.trim(),
        start_date: holidayForm.start_date,
        end_date: holidayForm.end_date || null,
        notes: holidayForm.notes || '',
      };
      if (editingHoliday?.id) {
        await api.patch(`/public-holidays/${editingHoliday.id}/`, payload);
        toast.success('Public holiday updated');
      } else {
        await api.post('/public-holidays/', payload);
        toast.success('Public holiday added');
      }
      setHolidayForm(initialPublicHoliday);
      setEditingHoliday(null);
      fetchAll();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save public holiday'));
    } finally {
      setHolidaySaving(false);
    }
  };

  const editPublicHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setEditingShopHoursGroup(null);
    setPendingShopHoursEntries([]);
    setShopHoursModalTab('holidays');
    setHolidayForm({
      name: holiday.name || '',
      start_date: holiday.start_date || currentBusinessDate,
      end_date: holiday.end_date || '',
      notes: holiday.notes || '',
    });
  };

  const deletePublicHoliday = async (holiday) => {
    if (!window.confirm(`Delete public holiday "${holiday.name}"?`)) return;
    try {
      await api.delete(`/public-holidays/${holiday.id}/`);
      toast.success('Public holiday deleted');
      if (editingHoliday?.id === holiday.id) {
        setEditingHoliday(null);
        setHolidayForm(initialPublicHoliday);
      }
      fetchAll();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete public holiday'));
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    if (activeTab === 'staff') {
      setStaffForm({ ...initialStaff, ...item, department: item.department || '' });
    } else if (activeTab === 'attendance') {
      const entryType = item.punch_type === 'out' ? 'punch_out' : 'punch_in';
      setAttendanceEntryType(entryType);
      // Notes are stored "<Reason> - <free text>". Split them back so the Reason
      // dropdown repopulates and Notes holds only the remainder — otherwise the
      // whole string landed in Notes and the reason was silently lost on re-save.
      const { reason: editReason, notes: editNotes } = splitPunchNotes(item.notes);
      setAttendanceReason(editReason);
      setAttendanceForm({ ...initialAttendance, ...item, notes: editNotes, punch_time: item.punch_time ? item.punch_time.slice(0, 16) : getLocalDateTimeInputValue() });
    } else if (activeTab === 'leave') {
      setLeaveEntryType(item.is_absent ? 'absent' : 'leave');
      setLeaveForm({ ...initialLeave, ...item, is_paid: item.is_paid !== false });
    } else if (activeTab === 'roster') {
      const sh = (!item.shift_start || !item.shift_end) ? getEffectiveShopHours(item.roster_date, getDayOfWeek(item.roster_date)) : null;
      setRosterForm({ ...initialRoster, ...item, shift_start: item.shift_start || (sh ? sh.open_time : ''), shift_end: item.shift_end || (sh ? sh.close_time : '') });
    } else if (activeTab === 'shop_hours') {
      if (shopHoursListTab === 'holidays') {
        editPublicHoliday(item);
        setShopHoursModalTab('holidays');
        setShowShopHoursModal(true);
        return;
      }
      openEditShopHoursGroup(item.effective_from, {
        open_time: item.open_time,
        close_time: item.close_time,
        notes: item.notes || '',
        days: [Number(item.day_of_week)],
        dayEntries: [{ day: Number(item.day_of_week), id: item.id }],
      });
      return;
    } else {
      setAdvanceForm({ ...initialAdvance, ...item });
    }
    setShowModal(true);
  };

  const openStaffEventEdit = (eventItem) => {
    setPendingStaffEventEntries([]);
    setEventForm({
      source: eventItem.source || getEventSourceFromType(eventItem.event_type),
      id: eventItem.id,
      staff: eventItem.staff || selectedStaffEventsStaff?.id || '',
      event_type: eventItem.event_type,
      event_date: eventItem.event_date,
      amount: eventItem.amount == null ? '' : String(eventItem.amount),
      status: eventItem.status || 'approved',
      reason: eventItem.reason || '',
      department: eventItem.department || '',
      monthly_salary: eventItem.monthly_salary == null ? '' : String(eventItem.monthly_salary),
      salary_date: eventItem.salary_date == null ? '' : String(eventItem.salary_date),
      employment_status_value: eventItem.employment_status_value || 'active',
    });
    setShowEventModal(true);
  };

  const openStaffEventCreate = (eventType = 'salary') => {
    setPendingStaffEventEntries([]);
    setEventForm({
      ...initialStaffEventForm,
      staff: selectedStaffEventsStaff?.id || '',
      source: getEventSourceFromType(eventType),
      event_type: eventType,
      event_date: currentBusinessDate,
      department: selectedStaffEventsStaff?.department || '',
      monthly_salary: selectedStaffEventsStaff?.monthly_salary == null ? '' : String(selectedStaffEventsStaff.monthly_salary),
      salary_date: selectedStaffEventsStaff?.salary_date == null ? '' : String(selectedStaffEventsStaff.salary_date || ''),
      employment_status_value: selectedStaffEventsStaff?.employment_status || 'active',
    });
    setShowEventModal(true);
  };

  const buildStaffEventEntry = (sourceForm = eventForm) => {
    const eventType = sourceForm.event_type;
    const source = getEventSourceFromType(eventType);
    const staffId = sourceForm.staff || selectedStaffEventsStaff?.id;
    if (!staffId) { toast.error('Select a staff member'); return null; }
    if (!sourceForm.event_date) { toast.error('Select an event date'); return null; }
    if (source === 'financial' && sourceForm.amount === '') { toast.error('Enter amount'); return null; }
    if (eventType === 'salary_change' && sourceForm.monthly_salary === '') { toast.error('Enter monthly salary'); return null; }
    if (eventType === 'salary_date_change' && sourceForm.salary_date === '') { toast.error('Enter salary due day'); return null; }
    return {
      ...sourceForm,
      _key: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      staff: staffId,
      source,
      event_type: eventType,
    };
  };

  const buildStaffEventSaveRequest = (entry) => {
    const isLifecycle = getEventSourceFromType(entry.event_type) === 'lifecycle';
    const endpoint = isLifecycle ? 'staff-lifecycle-events' : 'staff-advances';
    const payload = isLifecycle
      ? {
          staff: entry.staff || selectedStaffEventsStaff?.id,
          event_type: entry.event_type,
          event_date: entry.event_date,
          department: entry.event_type === 'department_change' ? (entry.department || null) : null,
          monthly_salary: entry.event_type === 'salary_change' ? (entry.monthly_salary === '' ? 0 : Number(entry.monthly_salary)) : null,
          salary_date: entry.event_type === 'salary_date_change' ? (entry.salary_date === '' ? null : Number(entry.salary_date)) : null,
          employment_status_value: entry.event_type === 'status_change' ? entry.employment_status_value : null,
          reason: entry.reason || '',
        }
      : {
          staff: entry.staff || selectedStaffEventsStaff?.id,
          payment_type: entry.event_type,
          advance_date: entry.event_date,
          amount: entry.amount === '' ? 0 : Number(entry.amount),
          status: entry.status || 'approved',
          repaid_amount: 0,
          reason: entry.reason || '',
        };
    return { endpoint, payload };
  };

  const saveStaffEventEntry = async (entry) => {
    const { endpoint, payload } = buildStaffEventSaveRequest(entry);
    if (entry.id) {
      await api.patch(`/${endpoint}/${entry.id}/`, payload);
    } else {
      await api.post(`/${endpoint}/`, payload);
    }
  };

  const handleApplyStaffEventPending = () => {
    const entry = buildStaffEventEntry();
    if (!entry) return;
    setPendingStaffEventEntries(prev => [...prev, entry]);
  };

  const handleDelete = async (id, item) => {
    if (!window.confirm('Delete this record?')) return;
    let endpoint;
    if (activeTab === 'staff') endpoint = 'staff';
    else if (activeTab === 'attendance') endpoint = 'staff-attendance';
    else if (activeTab === 'leave') endpoint = 'staff-leaves';
    else if (activeTab === 'roster') endpoint = 'staff-rosters';
    else if (activeTab === 'shop_hours') endpoint = shopHoursListTab === 'holidays' ? 'public-holidays' : 'shop-hours';
    else endpoint = 'staff-advances';
    try {
      await api.delete(`/${endpoint}/${id}/`);
      toast.success('Record deleted');
      fetchAll();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete record'));
    }
  };

  const togglePunchSelect = (id) => setSelectedPunches(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const setPunchSelection = (ids, selected) => setSelectedPunches(prev => {
    const next = new Set(prev);
    ids.forEach(id => { if (selected) next.add(id); else next.delete(id); });
    return next;
  });
  const bulkDeletePunches = async (idsArg) => {
    const ids = (idsArg && idsArg.length ? idsArg : Array.from(selectedPunches)).filter(Boolean);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} attendance entr${ids.length === 1 ? 'y' : 'ies'}? This cannot be undone.`)) return;
    try {
      const res = await api.post('/staff-attendance/bulk_delete/', { ids });
      const deleted = res.data?.deleted ?? ids.length;
      setSelectedPunches(new Set());
      toast.success(`Deleted ${deleted} entr${deleted === 1 ? 'y' : 'ies'}`);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to delete entries'));
    }
  };

  const handleToggleLeaveViolation = async (leaveItem) => {
    const nextEnabled = !leaveItem.has_violation;
    const actionLabel = nextEnabled ? 'mark this leave as a violation' : 'clear this leave violation';
    if (!window.confirm(`Do you want to ${actionLabel}?`)) return;
    try {
      await api.post(`/staff-leaves/${leaveItem.id}/set-violation/`, { enabled: nextEnabled });
      toast.success(nextEnabled ? 'Leave marked as violation' : 'Leave violation cleared');
      fetchAll();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update leave violation'));
    }
  };

  const handleSaveStaffEvent = async (e) => {
    e.preventDefault();
    const entries = eventForm.id
      ? [buildStaffEventEntry(eventForm)]
      : (pendingStaffEventEntries.length > 0 ? pendingStaffEventEntries : [buildStaffEventEntry(eventForm)]);
    if (entries.some(entry => !entry)) return;
    try {
      await Promise.all(entries.map(saveStaffEventEntry));
      toast.success(eventForm.id ? 'Event updated' : `${entries.length} event${entries.length === 1 ? '' : 's'} saved`);
      closeEventModal();
      fetchAll();
      if (selectedStaffEventsStaff) {
        loadStaffEvents(selectedStaffEventsStaff);
      }
    } catch (error) {
      toast.error(formatApiError(error, `Failed to ${eventForm.id ? 'update' : 'save'} event`));
    }
  };

  const handleDeleteStaffEvent = async (eventItem) => {
    if (!window.confirm('Delete this event?')) return;
    const endpoint = eventItem.source === 'financial' ? 'staff-advances' : 'staff-lifecycle-events';
    try {
      await api.delete(`/${endpoint}/${eventItem.id}/`);
      toast.success('Event deleted');
      fetchAll();
      if (selectedStaffEventsStaff) {
        loadStaffEvents(selectedStaffEventsStaff);
      }
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete event'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let endpoint, payload;
    if (activeTab === 'staff') {
      endpoint = 'staff';
      payload = {
        ...staffForm,
        department: staffForm.department || null,
        user: staffForm.user || null,
        monthly_salary: staffForm.monthly_salary === '' ? 0 : Number(staffForm.monthly_salary),
        salary_date: staffForm.salary_date === '' ? null : Number(staffForm.salary_date),
      };
    } else if (activeTab === 'attendance') {
      endpoint = 'staff-attendance';
      const notes = [attendanceReason, attendanceForm.notes]
        .map(value => String(value || '').trim())
        .filter(Boolean)
        .filter((value, index, values) => values.indexOf(value) === index)
        .join(' - ');
      payload = { ...attendanceForm, notes, punch_type: attendanceEntryType === 'punch_out' ? 'out' : 'in' };
    } else if (activeTab === 'leave') {
      endpoint = 'staff-leaves';
      const resolvedStatus = adminUser ? (leaveForm.status || 'approved') : 'approved';
      payload = {
        ...leaveForm,
        start_date: restrictedTodayOnly ? currentBusinessDate : leaveForm.start_date,
        end_date: restrictedTodayOnly ? currentBusinessDate : leaveForm.end_date,
        status: resolvedStatus,
        is_absent: adminUser && leaveEntryType === 'absent',
        leave_type: adminUser && leaveEntryType === 'absent' ? (leaveForm.leave_type || 'other') : leaveForm.leave_type
      };
    } else if (activeTab === 'roster') {
      endpoint = 'staff-rosters';
      payload = rosterForm;
    } else {
      endpoint = 'staff-advances';
      payload = { ...advanceForm, payment_type: activeTab === 'deductions' ? 'deduction' : advanceForm.payment_type, amount: advanceForm.amount === '' ? 0 : Number(advanceForm.amount), repaid_amount: advanceForm.repaid_amount === '' ? 0 : Number(advanceForm.repaid_amount) };
    }
    try {
      if (editingItem?.id) {
        await api.patch(`/${endpoint}/${editingItem.id}/`, payload);
        toast.success('Record updated');
      } else {
        await api.post(`/${endpoint}/`, payload);
        toast.success('Record created');
      }
      closeModal();
      fetchAll();
    } catch (error) {
      toast.error(formatApiError(error, `Failed to save ${tabs.find((tab) => tab.id === activeTab)?.label?.toLowerCase() || 'record'}`));
    }
  };

  const buildDateAwareStaffOptions = (targetDate, selectedValue = '') =>
    buildStaffDropdownOptions(staff, { onDate: targetDate, keepValue: selectedValue });
  const attendanceTargetDate = getBusinessDateForDateTimeValue(attendanceForm.punch_time, businessDayStartHour) || currentBusinessDate;
  const attendanceStaffOptions = useMemo(
    () => buildDateAwareStaffOptions(attendanceTargetDate, attendanceForm.staff),
    [staff, attendanceTargetDate, attendanceForm.staff]
  );
  const rosterStaffOptions = useMemo(
    () => buildDateAwareStaffOptions(rosterForm.roster_date || currentBusinessDate, rosterForm.staff),
    [staff, rosterForm.roster_date, rosterForm.staff, currentBusinessDate]
  );
  const advanceStaffOptions = useMemo(
    () => buildDateAwareStaffOptions(advanceForm.advance_date || currentBusinessDate, advanceForm.staff),
    [staff, advanceForm.advance_date, advanceForm.staff, currentBusinessDate]
  );
  const rosterInlineDateOptions = useMemo(() => {
    const monday = weekToMonday(rosterInline.week);
    if (!monday) return [];
    const selectedDates = rosterInline.days.length
      ? rosterInline.days.map((dayIdx) => {
          const date = new Date(monday);
          date.setDate(monday.getDate() + dayIdx);
          return isoDate(date);
        })
      : [isoDate(monday)];
    return staff
      .filter((member) => selectedDates.some((dateStr) => isStaffEligibleOnDate(member, dateStr)) || String(member.id) === String(rosterInline.staff))
      .map((member) => ({ value: member.id, label: getStaffOptionLabel(member, member.employment_status === 'terminated') }));
  }, [staff, rosterInline.week, rosterInline.days, rosterInline.staff]);
  const smartAttendanceTargetDate = smartAttDate || currentBusinessDate;
  const smartAttendanceStaffOptions = useMemo(
    () => buildDateAwareStaffOptions(smartAttendanceTargetDate),
    [staff, smartAttendanceTargetDate]
  );
  // Top filter (attendance / leave / roster / advances lists) — only staff in service during the
  // selected date range, with the time-bound attributed label (terminated shown when in-range).
  const historyStaffOptions = useMemo(
    () => buildStaffDropdownOptions(staff, { fromDate: startDate, toDate: endDate, keepValue: staffFilter }),
    [staff, startDate, endDate, staffFilter]
  );
  const leaveTargetDate = restrictedTodayOnly
    ? currentBusinessDate
    : (leaveForm.start_date || leaveForm.end_date || currentBusinessDate);
  const leaveStaffOptions = useMemo(
    () => buildStaffDropdownOptions(staff, { onDate: leaveTargetDate, keepValue: leaveForm.staff }),
    [staff, leaveTargetDate, leaveForm.staff]);
  const leaveQuickStaffOptions = useMemo(
    () => buildStaffDropdownOptions(staff, { onDate: leaveQuick.date || currentBusinessDate, keepValue: leaveQuick.staff }),
    [staff, leaveQuick.date, leaveQuick.staff, currentBusinessDate]);

  const staffTabs = useMemo(() => tabs.filter(t => {
    if (t.id === 'staff') return canViewStaff;
    if (t.id === 'attendance') return canViewAttendance;
    if (t.id === 'leave') return false; // merged into the Attendance tab ("Add Leave" button)
    if (t.id === 'roster') return canViewStaff;
    if (t.id === 'shop_hours') return adminUser;
    if (t.id === 'advances') return canViewStaff;
    if (t.id === 'salary_schedule') return canViewStaff;
    if (t.id === 'deductions') return canViewStaff;
    if (t.id === 'performance') return hasPermission('performance', 'view');
    if (t.id === 'staff_dashboard') return canViewStaff || adminUser;
    return false;
  }), [adminUser, canViewAttendance, canViewLeaves, canViewStaff, hasPermission]);

  useEffect(() => {
    if (staffTabs.length === 0) return;
    if (!staffTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(staffTabs[0].id);
    }
  }, [activeTab, staffTabs]);

  const staffTabGridClass = staffTabs.length <= 1
    ? 'grid-cols-1'
    : staffTabs.length === 2
      ? 'grid-cols-2'
      : staffTabs.length === 3
        ? 'grid-cols-3'
        : 'grid-cols-2 md:grid-cols-4 lg:grid-cols-7';

  const openFillPlaceholder = (item) => {
    setEditingItem(null);
    // Pre-fill the reason for an auto return-IN (break return) so only the time is left to enter.
    setAttendanceReason(item._pendingReason || '');
    setAttendanceForm({ ...initialAttendance, staff: item.staff, punch_type: item.punch_type || 'in',
      punch_time: item._date ? `${item._date}T${new Date().toTimeString().slice(0,5)}` : getLocalDateTimeInputValue() });
    setShowModal(true);
  };

  const parseAttTime = (t) => {
    if (!t) return null; t = t.trim();
    const fd = t.match(/^(\d{1,2})(\d{2})$/);
    if (fd) { const h = parseInt(fd[1]); return (h >= 0 && h <= 23) ? `${String(h).padStart(2,'0')}:${fd[2]}` : null; }
    const cf = t.match(/^(\d{1,2}):(\d{2})$/);
    if (cf) { const h = parseInt(cf[1]); return (h >= 0 && h <= 23) ? `${String(h).padStart(2,'0')}:${cf[2]}` : null; }
    const ap = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
    if (ap) { let h = parseInt(ap[1]); const m = ap[2]||'00'; const mer = ap[3].toLowerCase(); if (mer==='pm'&&h<12) h+=12; if (mer==='am'&&h===12) h=0; return `${String(h).padStart(2,'0')}:${m}`; }
    return null;
  };
  const matchStaffMember = (name) => {
    const nl = name.toLowerCase().trim();
    return staff.find(s => (s.full_name||`${s.first_name} ${s.last_name}`).toLowerCase().trim()===nl)
      || staff.find(s => s.first_name?.toLowerCase()===nl)
      || staff.find(s => (s.full_name||`${s.first_name} ${s.last_name}`).toLowerCase().includes(nl))
      || null;
  };
  // Parse a stand-alone date-header line into YYYY-MM-DD. Handles full & abbreviated month
  // names ("1 Sep 2024", "Mon, 1 September 2024", "Sep 1, 2024") and numeric formats
  // ("2024-09-01", "1/9/2024" as DD/MM/YYYY). Returns null if the line isn't a date.
  const parseHeaderDate = (line) => {
    const MONS_FULL = ['january','february','march','april','may','june','july','august','september','october','november','december'];
    const monthIdx = (m) => MONS_FULL.findIndex((x) => x.startsWith(m.toLowerCase().slice(0, 3)));
    let m = line.match(/^(?:\w+\.?,?\s+)?(\d{1,2})\s+([A-Za-z]+)\.?,?\s+(\d{4})$/); // 1 Sep 2024 / Mon, 1 September 2024
    if (m) { const mi = monthIdx(m[2]); if (mi >= 0) return `${m[3]}-${String(mi + 1).padStart(2, '0')}-${m[1].padStart(2, '0')}`; }
    m = line.match(/^([A-Za-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})$/); // Sep 1, 2024
    if (m) { const mi = monthIdx(m[1]); if (mi >= 0) return `${m[3]}-${String(mi + 1).padStart(2, '0')}-${m[2].padStart(2, '0')}`; }
    m = line.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/); // 2024-09-01
    if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
    m = line.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/); // 1/9/2024 (DD/MM/YYYY)
    if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    return null;
  };
  const handleSmartAttParse = () => {
    const today = new Date().toISOString().slice(0,10);
    let detectedDate = today;
    const rows = [];
    for (const line of smartAttText.split('\n').map(l=>l.trim()).filter(Boolean)) {
      const hd = parseHeaderDate(line);
      if (hd) { detectedDate = hd; continue; }
      if (/^total\b/i.test(line)) continue;
      const parts = line.split(/\s*-\s*/);
      if (parts.length < 2) continue;
      const staffName = parts[0].trim();
      const punchTypeRaw = (parts[1]?.trim()||'').toLowerCase();
      const matched = matchStaffMember(staffName);
      // Off-day types (Leave / Absentee / Holiday) route to the Performance path; In/Out are punches.
      // Template: Name - Type - Time - Reason - Notes  (Reason = first segment after time, Notes = rest).
      const OFF_TYPES = { leave: 'leave', absent: 'absentee', absentee: 'absentee', holiday: 'holiday', off: 'leave' };
      if (OFF_TYPES[punchTypeRaw]) {
        const off_type = OFF_TYPES[punchTypeRaw];
        const maybeTime = parseAttTime(parts[2]?.trim() || '');
        const time = maybeTime || '';
        const descParts = maybeTime ? parts.slice(3) : parts.slice(2);
        const reason = (descParts[0] || '').trim();
        const notes = descParts.slice(1).join(' - ').trim();
        rows.push({ staffId: matched?.id||'', staffName, kind: 'offday', off_type, date: detectedDate, time, reason, notes, is_warning: off_type !== 'absentee' });
        continue;
      }
      const timeRaw = parts[2]?.trim()||'';
      const notes = parts.slice(3).join(' - ').trim();
      const punch_type = punchTypeRaw==='out' ? 'out' : 'in';
      const time = parseAttTime(timeRaw) || new Date().toISOString().slice(11,16);
      rows.push({ staffId: matched?.id||'', staffName, kind: 'punch', punch_type, date: detectedDate, time, notes });
    }
    setSmartAttDate(rows[0]?.date || today);
    setSmartAttRows(rows);
    setSmartAttStep('confirm');
  };
  const handleSmartAttSave = async () => {
    setSmartAttSaving(true);
    let saved = 0, failed = 0;
    for (const row of smartAttRows) {
      if (!row.staffId) { failed++; continue; }
      try {
        if (row.kind === 'offday') {
          await api.post('/staff-performance/', { staff: row.staffId, violation_type: row.off_type, date: row.date || smartAttDate, is_warning: !!row.is_warning, reason: row.reason || '', description: row.notes || '' });
        } else {
          await api.post('/staff-attendance/', { staff: row.staffId, punch_type: row.punch_type, punch_time: `${row.date || smartAttDate}T${row.time || '00:00'}:00`, notes: row.notes });
        }
        saved++;
      } catch { failed++; }
    }
    setSmartAttSaving(false);
    setShowSmartAttModal(false); setSmartAttText(''); setSmartAttRows([]); setSmartAttStep('input');
    if (saved > 0) { toast.success(`${saved} record${saved!==1?'s':''} saved${failed>0?`, ${failed} failed`:''}`); fetchAll(); }
    else toast.error(`No records saved${failed>0?` (${failed} failed)`:''}`); 
  };

  const renderModalForm = () => {
    if (activeTab === 'staff') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
            <input value={staffForm.first_name} onChange={(e) => setStaffForm({ ...staffForm, first_name: e.target.value })} placeholder="First name" className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input value={staffForm.last_name} onChange={(e) => setStaffForm({ ...staffForm, last_name: e.target.value })} placeholder="Last name" className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} placeholder="Phone" className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} placeholder="Email" className="w-full px-3 py-2 border rounded-lg" />
          </div>
          {!editingItem && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select value={staffForm.department} onChange={(e) => setStaffForm({ ...staffForm, department: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                <option value="">No Department</option>
                {departments.map((dept) => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
            <input value={staffForm.designation} onChange={(e) => setStaffForm({ ...staffForm, designation: e.target.value })} placeholder="Designation" className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date *</label>
            <UiDateField value={staffForm.hire_date} onChange={(value) => setStaffForm({ ...staffForm, hire_date: value })} />
          </div>
          {!editingItem && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
              <select value={staffForm.employment_status} onChange={(e) => setStaffForm({ ...staffForm, employment_status: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="on_leave">On Leave</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          )}
          {!editingItem && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Salary</label>
              <input type="number" value={staffForm.monthly_salary} onChange={(e) => setStaffForm({ ...staffForm, monthly_salary: e.target.value })} placeholder="0.00" className="w-full px-3 py-2 border rounded-lg" min="0" step="0.01" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salary Due Day</label>
            <input type="number" value={staffForm.salary_date} onChange={(e) => setStaffForm({ ...staffForm, salary_date: e.target.value })} placeholder="1-31" className="w-full px-3 py-2 border rounded-lg" min="1" max="31" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pay Type</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setStaffForm({ ...staffForm, wage_type: 'salaried' })} className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition ${staffForm.wage_type === 'salaried' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Monthly Salaried</button>
              <button type="button" onClick={() => setStaffForm({ ...staffForm, wage_type: 'daily_wages' })} className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition ${staffForm.wage_type === 'daily_wages' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Daily Wages</button>
            </div>
            {editingItem && (
              <div className="mt-2 space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {staffForm.wage_type !== editingItem.wage_type ? 'Effective from (pay before this date keeps the old type)' : 'Effective date for a pay-type change (pick before switching)'}
                  </label>
                  <UiDateField value={staffForm.wage_type_effective_date} onChange={(val) => setStaffForm({ ...staffForm, wage_type_effective_date: val })} placeholder="Defaults to today" />
                  <p className="text-[11px] text-gray-400 mt-1">May be a past date. A change applies onward from this date; earlier pay keeps the previous type.</p>
                </div>
                {wageTypeHistory.length > 1 && (
                  <div className="border-t border-gray-100 pt-2">
                    <p className="text-[11px] font-semibold text-gray-500 mb-1">Pay-type history</p>
                    <div className="space-y-1">
                      {wageTypeHistory.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-[11px]">
                          <span className="text-gray-500">{p.from || '—'} → {p.to || 'ongoing'}</span>
                          <span className={`font-medium ${getWageTypeClass(p.type)}`}>{getWageTypeLabel(p.type)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Name</label>
            <input value={staffForm.emergency_contact_name} onChange={(e) => setStaffForm({ ...staffForm, emergency_contact_name: e.target.value })} placeholder="Emergency contact name" className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Phone</label>
            <input value={staffForm.emergency_contact_phone} onChange={(e) => setStaffForm({ ...staffForm, emergency_contact_phone: e.target.value })} placeholder="Emergency contact phone" className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Linked User Account</label>
            <select value={staffForm.user || ''} onChange={(e) => setStaffForm({ ...staffForm, user: e.target.value || null })} className="w-full px-3 py-2 border rounded-lg">
              <option value="">— None —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.username} ({u.role_name || u.role})</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">Links a login account so the staff member can see their own dashboard.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <textarea value={staffForm.address} onChange={(e) => setStaffForm({ ...staffForm, address: e.target.value })} placeholder="Address" className="w-full px-3 py-2 border rounded-lg" rows="2" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={staffForm.notes} onChange={(e) => setStaffForm({ ...staffForm, notes: e.target.value })} placeholder="Notes" className="w-full px-3 py-2 border rounded-lg" rows="3" />
          </div>
        </div>
      );
    }

    if (activeTab === 'attendance') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Date & time lead — they determine who was employed and their wage type that day. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Punch Time *</label>
              <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_9rem] gap-3">
                <UiDateField
                  value={attendanceFormDateTime.date}
                  onChange={(value) => setAttendanceForm((prev) => ({
                    ...prev,
                    punch_time: mergeDateTimeInputValue(value, attendanceFormDateTime.time, currentBusinessDate),
                  }))}
                />
                <UiTimeField
                  value={attendanceFormDateTime.time}
                  onChange={(value) => setAttendanceForm((prev) => ({
                    ...prev,
                    punch_time: mergeDateTimeInputValue(attendanceFormDateTime.date, value, currentBusinessDate),
                  }))}
                />
              </div>
              {attendanceTargetDate && (
                <p className="text-[11px] text-gray-500 mt-1"><sub className="text-[10px] text-gray-400">{getShopHoursLabel(attendanceTargetDate)}</sub></p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Punch Type *</label>
              <select value={attendanceEntryType} onChange={(e) => setAttendanceEntryType(e.target.value)} className="w-full px-3 py-2 border rounded-lg" required>
                <option value="punch_in">In</option>
                <option value="punch_out">Out</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
              <select value={attendanceForm.staff} onChange={(e) => setAttendanceForm({ ...attendanceForm, staff: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required>
                <option value="">Select staff</option>
                {attendanceStaffOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              {attendanceForm.staff && (() => { const m = staff.find(s => String(s.id) === String(attendanceForm.staff)); return (
                <p className="text-[11px] text-gray-500 mt-1">{m ? <>On {attendanceTargetDate}: <span className="font-medium">{getStaffWageTypeOnDate(m, attendanceTargetDate) === 'daily_wages' ? 'Daily-wage' : 'Salaried'}</span> · </> : null}Schedule <sub className="text-[10px] text-gray-400">{getStaffScheduleLabel(attendanceForm.staff, attendanceTargetDate)}</sub></p>
              ); })()}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <select
                value={attendanceReason}
                onChange={(e) => setAttendanceReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg mb-2"
              >
                <option value="">Select a reason...</option>
                {ATTENDANCE_REASONS.map((r) => (<option key={r} value={r}>{r}</option>))}
              </select>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={attendanceForm.notes} onChange={(e) => setAttendanceForm({ ...attendanceForm, notes: e.target.value })} placeholder="Notes" className="w-full px-3 py-2 border rounded-lg" rows="2" />
            </div>
          </div>
          <button type="button" onClick={handleApplyAttendancePending} className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2">
            Apply
          </button>
          {pendingAttendanceEntries.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pending ({pendingAttendanceEntries.length} entries)</p>
              <div className="border rounded-lg divide-y overflow-hidden max-h-60 overflow-y-auto">
                {pendingAttendanceEntries.map((e, i) => (
                  <div key={e._key} className="flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${e.punch_type === 'in' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{e.punch_type}</span>
                      <span className="text-gray-700 font-medium">{e.staff_name}</span>
                      <span className="text-gray-500 text-xs">{e.punch_time}</span>
                      {e.notes && <span className="text-gray-400 text-xs">{e.notes}</span>}
                    </div>
                    <button onClick={() => setPendingAttendanceEntries(p => p.filter((_, j) => j !== i))} className="p-1 text-gray-400 hover:text-red-600 rounded"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'leave') {
      const selectedLeaveStaff = staff.find((s) => String(s.id) === String(leaveForm.staff));
      const isDailyWage = (selectedLeaveStaff?.wage_type || '') === 'daily_wages';
      // Daily-wage staff get no paid leave — force Unpaid.
      const effectiveIsPaid = isDailyWage ? false : (leaveForm.is_paid !== false);
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
              <select value={leaveForm.staff} onChange={(e) => setLeaveForm({ ...leaveForm, staff: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required>
                <option value="">Select staff</option>
                {leaveStaffOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select
                value={effectiveIsPaid ? 'paid' : 'unpaid'}
                onChange={(e) => setLeaveForm({ ...leaveForm, is_paid: e.target.value === 'paid' })}
                className="w-full px-3 py-2 border rounded-lg"
                disabled={isDailyWage}
                required
              >
                {!isDailyWage && <option value="paid">Paid</option>}
                <option value="unpaid">Unpaid</option>
              </select>
              {isDailyWage && <p className="text-[11px] text-gray-400 mt-1">Daily-wage staff have no paid leave.</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
              <select value={leaveForm.leave_type === 'unpaid' || leaveForm.leave_type === 'annual' ? 'other' : leaveForm.leave_type} onChange={(e) => setLeaveForm({ ...leaveForm, leave_type: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required>
                <option value="casual">Casual</option>
                <option value="sick">Sick</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <UiDateField
                value={restrictedTodayOnly ? currentBusinessDate : leaveForm.start_date}
                onChange={(value) => !restrictedTodayOnly && setLeaveForm({ ...leaveForm, start_date: value })}
                disabled={restrictedTodayOnly}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <UiDateField
                value={restrictedTodayOnly ? currentBusinessDate : leaveForm.end_date}
                onChange={(value) => !restrictedTodayOnly && setLeaveForm({ ...leaveForm, end_date: value })}
                disabled={restrictedTodayOnly}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <textarea value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} placeholder="Reason" className="w-full px-3 py-2 border rounded-lg" rows="2" />
            </div>
            {false && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={leaveForm.status || 'approved'} onChange={(e) => setLeaveForm({ ...leaveForm, status: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            )}
          </div>
          <button type="button" onClick={handleApplyLeavePending} className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2">
            Apply
          </button>
          {pendingLeaveEntries.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pending ({pendingLeaveEntries.length} entries)</p>
              <div className="border rounded-lg divide-y overflow-hidden max-h-60 overflow-y-auto">
                {pendingLeaveEntries.map((e, i) => (
                  <div key={e._key} className="flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${e.is_paid === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{e.is_paid === false ? 'Unpaid' : 'Paid'}</span>
                      <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700 capitalize">{e.leave_type}</span>
                      <span className="text-gray-700 font-medium">{e.staff_name}</span>
                      <span className="text-gray-500 text-xs">{fmtDate(e.start_date)}{e.end_date && e.end_date !== e.start_date ? ` → ${fmtDate(e.end_date)}` : ''}</span>
                      {e.reason && <span className="text-gray-400 text-xs">{e.reason}</span>}
                    </div>
                    <button onClick={() => setPendingLeaveEntries(p => p.filter((_, j) => j !== i))} className="p-1 text-gray-400 hover:text-red-600 rounded"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'roster') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
            <select value={rosterForm.staff} onChange={(e) => setRosterForm({ ...rosterForm, staff: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required>
              <option value="">Select staff</option>
              {rosterStaffOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Roster Date *</label>
            <UiDateField value={rosterForm.roster_date} onChange={(value) => {
              const sh = getEffectiveShopHours(value, getDayOfWeek(value));
              setRosterForm((f) => ({ ...f, roster_date: value,
                shift_start: sh ? String(sh.open_time).slice(0, 5) : f.shift_start,
                shift_end: sh ? String(sh.close_time).slice(0, 5) : f.shift_end }));
            }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Shift Start</label>
            <UiTimeField value={rosterForm.shift_start} onChange={(value) => setRosterForm({ ...rosterForm, shift_start: value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Shift End</label>
            <UiTimeField value={rosterForm.shift_end} onChange={(value) => setRosterForm({ ...rosterForm, shift_end: value })} />
          </div>
          <div className="md:col-span-2 -mt-2">
            <p className="text-xs text-gray-400">Defaults to {getShopHoursLabel(rosterForm.roster_date)} for this date — edit if the staff shift differs, then save.</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={rosterForm.notes} onChange={(e) => setRosterForm({ ...rosterForm, notes: e.target.value })} placeholder="Notes" className="w-full px-3 py-2 border rounded-lg" rows="3" />
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Staff *</label>
          <select value={advanceForm.staff} onChange={(e) => setAdvanceForm({ ...advanceForm, staff: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required>
            <option value="">Select staff</option>
                {advanceStaffOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          {advanceForm.staff && (() => {
            const member = staff.find((s) => String(s.id) === String(advanceForm.staff));
            if (!member) return null;
            const summary = getEffectiveSalaryDueSummary(member, advances, currentBusinessDate, { rollOverdue: true });
            const due = roundSalaryDisplayAmount(summary?.unpaidAsOfToday || 0);
            const wageLabel = (member.wage_type === 'daily_wages') ? 'daily-wage, pro-rata' : 'salaried, pro-rata';
            return (
              <p className="text-[11px] text-primary-600 mt-1">
                Salary due as of today ({wageLabel}): <span className="font-semibold">{formatSalaryCurrency(due)}</span>
              </p>
            );
          })()}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
          <UiDateField value={advanceForm.advance_date} onChange={(value) => setAdvanceForm({ ...advanceForm, advance_date: value })} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type *</label>
          <select value={activeTab === 'deductions' ? 'deduction' : advanceForm.payment_type} onChange={(e) => setAdvanceForm({ ...advanceForm, payment_type: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required disabled={activeTab === 'deductions'}>
            <option value="advance">Advance / Loan</option>
            <option value="bonus">Performance Bonus</option>
            <option value="gift">🎁 Gift</option>
            <option value="salary">Salary Payment</option>
            <option value="deduction">Deduction</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
          <input type="number" value={advanceForm.amount} onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })} placeholder="0.00" className="w-full px-3 py-2 border rounded-lg" min="0" step="0.01" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Repaid Amount</label>
          <input type="number" value={advanceForm.repaid_amount} onChange={(e) => setAdvanceForm({ ...advanceForm, repaid_amount: e.target.value })} placeholder="0.00" className="w-full px-3 py-2 border rounded-lg" min="0" step="0.01" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
          <select value={advanceForm.status} onChange={(e) => setAdvanceForm({ ...advanceForm, status: e.target.value })} className="w-full px-3 py-2 border rounded-lg" required>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="paid">Paid</option>
            <option value="rejected">Rejected</option>
            <option value="settled">Settled</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
          <textarea value={advanceForm.reason} onChange={(e) => setAdvanceForm({ ...advanceForm, reason: e.target.value })} placeholder="Reason" className="w-full px-3 py-2 border rounded-lg" rows="3" />
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div></div>;
  }

  const handleQuickAddAttendance = async () => {
    if (!attQuick.staff) { toast.error('Select a staff member'); return; }
    setAttSaving(true);
    try {
      await api.post('/staff-attendance/', { staff: attQuick.staff, punch_type: attQuick.type, punch_time: `${attQuick.date}T${attQuick.time}:00`, notes: attQuick.notes });
      toast.success(attQuick.type === 'in' ? 'In recorded' : 'Out recorded');
      setAttQuick(q => ({ ...q, notes: '' }));
      fetchAll();
    } catch (error) { toast.error(formatApiError(error, 'Failed to record attendance')); }
    finally { setAttSaving(false); }
  };

  const handleQuickAddLeave = async () => {
    const quickStaffObj = leaveQuickStaffOptions.find(option => String(option.value) === String(leaveQuick.staff));
    if (!leaveQuick.staff || !quickStaffObj) { toast.error('Select a staff member'); return; }
    setLeaveSaving(true);
    try {
      await api.post('/staff-leaves/', { staff: leaveQuick.staff, is_absent: leaveQuick.type === 'absent', leave_type: leaveQuick.type === 'absent' ? 'other' : leaveQuick.leave_type, start_date: leaveQuick.date, end_date: leaveQuick.date, status: 'approved', reason: leaveQuick.notes });
      toast.success(leaveQuick.type === 'absent' ? 'Absence recorded' : 'Leave recorded');
      setLeaveQuick(q => ({ ...q, notes: '' }));
      fetchAll();
    } catch (error) { toast.error(formatApiError(error, 'Failed to record leave')); }
    finally { setLeaveSaving(false); }
  };

  const handleApplyRosterPending = () => {
    if (!rosterInline.allStaff && !rosterInline.staff) { toast.error('Select a staff member'); return; }
    if (rosterInline.days.length === 0) { toast.error('Select at least one day'); return; }
    if (!rosterInline.week) { toast.error('Select a week'); return; }
    const monday = weekToMonday(rosterInline.week);
    const targets = rosterInline.allStaff ? rosterInlineDateOptions : rosterInlineDateOptions.filter(o => String(o.value) === String(rosterInline.staff));
    const newEntries = targets.flatMap(staffObj =>
      rosterInline.days.map(dayIdx => {
        const d = new Date(monday); d.setDate(monday.getDate() + dayIdx);
        return { _key: `${Date.now()}_${staffObj.value}_${dayIdx}`, staff: staffObj.value, staff_name: staffObj.label || '', roster_date: isoDate(d), day_abbr: DAY_ABBREVS[dayIdx], shift_start: rosterInline.shift_start || null, shift_end: rosterInline.shift_end || null };
      })
    );
    setPendingRosterEntries(prev => [...prev, ...newEntries]);
  };

  const handleSaveAllRoster = async () => {
    if (pendingRosterEntries.length === 0) { toast.error('No entries to save'); return; }
    setRosterSaving(true);
    try {
      await Promise.all(pendingRosterEntries.map(e =>
        api.post('/staff-rosters/', { staff: e.staff, roster_date: e.roster_date, shift_start: e.shift_start, shift_end: e.shift_end })
      ));
      toast.success(`${pendingRosterEntries.length} roster entries saved`);
      setShowRosterModal(false);
      setPendingRosterEntries([]);
      fetchAll();
    } catch (error) { toast.error(formatApiError(error, 'Failed to save roster entries')); }
    finally { setRosterSaving(false); }
  };

  const handleAddRosterInline = async () => { await handleSaveAllRoster(); };

  const handleApplyAttendancePending = () => {
    if (!attendanceForm.staff) { toast.error('Select a staff member'); return; }
    if (!attendanceForm.punch_time) { toast.error('Enter punch time'); return; }
    const staffObj = attendanceStaffOptions.find(o => String(o.value) === String(attendanceForm.staff));
    const notes = [attendanceReason, attendanceForm.notes]
      .map(value => String(value || '').trim())
      .filter(Boolean)
      .filter((value, index, values) => values.indexOf(value) === index)
      .join(' - ');
    const newEntry = {
      _key: `${Date.now()}`,
      id: editingItem?.id || null,
      staff: attendanceForm.staff,
      staff_name: staffObj?.label || '',
      punch_type: attendanceEntryType === 'punch_out' ? 'out' : 'in',
      punch_time: attendanceForm.punch_time,
      notes
    };
    setPendingAttendanceEntries(prev => [...prev, newEntry]);
  };

  const handleSaveAllAttendance = async () => {
    if (pendingAttendanceEntries.length === 0) { toast.error('No entries to save'); return; }
    setAttSaving(true);
    try {
      // One atomic request — no partial saves if one entry fails.
      await api.post('/staff-attendance/bulk-save/', {
        entries: pendingAttendanceEntries.map(e => ({ id: e.id, staff: e.staff, punch_type: e.punch_type, punch_time: e.punch_time, notes: e.notes })),
      });
      toast.success(`${pendingAttendanceEntries.length} attendance entries saved`);
      setShowModal(false);
      setPendingAttendanceEntries([]);
      fetchAll();
    } catch (error) { toast.error(formatApiError(error, 'Failed to save attendance entries')); }
    finally { setAttSaving(false); }
  };

  const handleApplyLeavePending = () => {
    if (!leaveForm.staff) { toast.error('Select a staff member'); return; }
    const leaveStartDate = restrictedTodayOnly ? currentBusinessDate : leaveForm.start_date;
    const leaveEndDate = restrictedTodayOnly ? currentBusinessDate : leaveForm.end_date;
    if (!leaveStartDate) { toast.error('Enter start date'); return; }
    if (!leaveEndDate) { toast.error('Enter end date'); return; }
    const staffObj = leaveStaffOptions.find(o => String(o.value) === String(leaveForm.staff));
    const selectedStaff = staff.find((s) => String(s.id) === String(leaveForm.staff));
    const isDailyWage = (selectedStaff?.wage_type || '') === 'daily_wages';
    // Daily-wage staff have no paid leave.
    const isPaid = isDailyWage ? false : (leaveForm.is_paid !== false);
    const leaveType = ['casual', 'sick', 'other'].includes(leaveForm.leave_type) ? leaveForm.leave_type : 'other';
    const newEntry = {
      _key: `${Date.now()}`,
      id: editingItem?.id || null,
      staff: leaveForm.staff,
      staff_name: staffObj?.label || '',
      is_paid: isPaid,
      is_absent: false,
      leave_type: leaveType,
      start_date: leaveStartDate,
      end_date: leaveEndDate,
      reason: leaveForm.reason || '',
      status: 'approved'
    };
    setPendingLeaveEntries(prev => [...prev, newEntry]);
  };

  const handleSaveAllLeave = async () => {
    if (pendingLeaveEntries.length === 0) { toast.error('No entries to save'); return; }
    setLeaveSaving(true);
    try {
      await Promise.all(pendingLeaveEntries.map(e => {
        const payload = { staff: e.staff, is_paid: e.is_paid !== false, is_absent: false, leave_type: e.leave_type, start_date: e.start_date, end_date: e.end_date, status: 'approved', reason: e.reason };
        return e.id
          ? api.patch(`/staff-leaves/${e.id}/`, payload)
          : api.post('/staff-leaves/', payload);
      }));
      toast.success(`${pendingLeaveEntries.length} leave entries saved`);
      setShowModal(false);
      setPendingLeaveEntries([]);
      fetchAll();
    } catch (error) { toast.error(formatApiError(error, 'Failed to save leave entries')); }
    finally { setLeaveSaving(false); }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Staff Management</h1>
          <p className="text-sm sm:text-base text-gray-500">
            {adminUser ? 'Manage staff profiles, in/out punches, leave, roster, and salary advances' : 'Record your attendance punch in/out'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {activeTab === 'attendance' && adminUser && (
            <button onClick={() => { setShowSmartAttModal(true); setSmartAttStep('input'); setSmartAttText(''); }} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              Smart Paste
            </button>
          )}
          {activeTab === 'performance' && hasPermission('performance', 'edit') && (
            <button onClick={() => setPerfPenaltyTrigger(t => t + 1)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              Penalty
            </button>
          )}
          {activeTab !== 'staff' && activeTab !== 'performance' && activeTab !== 'salary_schedule' && activeTab !== 'advances' && activeTab !== 'shop_hours' && activeTab !== 'roster' && activeTab !== 'staff_dashboard' && (
            <button onClick={openCreateModal} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              {adminUser ? (activeTab === 'attendance' ? 'Add Record' : `Add ${tabs.find(t => t.id === activeTab)?.label}`) : activeTab === 'leave' ? 'Add Leave' : 'Add Attendance'}
            </button>
          )}
          {activeTab === 'attendance' && (
            <button onClick={() => setShowAddLeave(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              Add Off-Day
            </button>
          )}
          {activeTab === 'advances' && (
            <div className="text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-lg">
              Add payments via Expense page
            </div>
          )}
          {activeTab === 'salary_schedule' && (
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-lg">
                Salary paid via Expense
              </div>
              <button
                type="button"
                onClick={() => setShowSalaryHelp(true)}
                className="flex items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-100"
              >
                <HelpCircle size={16} /> Help
              </button>
            </div>
          )}
          {activeTab === 'performance' && hasPermission('performance', 'add') && (
            <button onClick={() => setPerfAddTrigger(t => t + 1)} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              Add Violation
            </button>
          )}
        </div>
      </div>

      <div className={`grid ${staffTabGridClass} gap-3`}>
        {staffTabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`p-3 rounded-xl border text-left ${activeTab === tab.id ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
            <div className="font-medium text-sm">{tab.label}</div>
          </button>
        ))}
      </div>

      {(adminUser || hasPermission('staff', 'view') || hasPermission('staff', 'add') || hasPermission('attendance', 'view') || hasPermission('attendance', 'add')) && activeTab !== 'staff' && activeTab !== 'shop_hours' && activeTab !== 'salary_schedule' && activeTab !== 'performance' && activeTab !== 'roster' && activeTab !== 'staff_dashboard' && (
        <UiDateRangeBlock
          preset={datePreset}
          presetOptions={(() => {
            if (restrictedTodayOnly) return buildStandardPresetOptions(['today']);
            const cv = activeTab === 'attendance' ? attView : activeTab === 'leave' ? leaveView : activeTab === 'roster' ? rosterView : activeTab === 'deductions' ? deductionsView : activeTab === 'advances' ? advancesView : 'table';
            return (cv === 'calendar' || cv === 'graph')
              ? buildStandardPresetOptions(['month', 'year'])
              : buildStandardPresetOptions(['today', 'week', 'month', 'year', 'custom']);
          })()}
          onPresetChange={applyStaffDatePreset}
          onPrevious={() => shiftStaffVisibleRange(-1)}
          onNext={() => shiftStaffVisibleRange(1)}
          periodPicker={!restrictedTodayOnly && getCurrentStaffCalendarView() === 'calendar'}
          onPeriodChange={handleStaffCalendarPeriodChange}
          fromDate={restrictedTodayOnly ? currentBusinessDate : startDate}
          onFromDateChange={handleStaffStartDateChange}
          toDate={restrictedTodayOnly ? currentBusinessDate : filterEndCalendarDate}
          onToDateChange={handleStaffEndDateChange}
          fromTime={startTime}
          onFromTimeChange={setStartTime}
          toTime={endTime}
          onToTimeChange={setEndTime}
          showTime={!restrictedTodayOnly}
          className="border-gray-200"
          rangeText={!restrictedTodayOnly && staffSelectedRangeLabel ? `Selected range: ${staffSelectedRangeLabel}` : ''}
          header={(
            <div className="flex flex-wrap items-center gap-2">
            {restrictedTodayOnly && (
              <span className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 border border-blue-100">
                Today only - table view
              </span>
            )}
            {['attendance', 'leave', 'roster', 'advances', 'deductions'].includes(activeTab) && !restrictedTodayOnly && (() => {
              const cv = activeTab === 'attendance' ? attView : activeTab === 'leave' ? leaveView : activeTab === 'roster' ? rosterView : activeTab === 'deductions' ? deductionsView : advancesView;
              const sv = (v) => { if (activeTab === 'attendance') setAttView(v); else if (activeTab === 'leave') setLeaveView(v); else if (activeTab === 'roster') setRosterView(v); else if (activeTab === 'deductions') setDeductionsView(v); else setAdvancesView(v); };
              return (
                <UiViewToggle
                  options={[
                    { value: 'table', label: 'Table' },
                    { value: 'calendar', label: 'Calendar' },
                    ...(activeTab === 'attendance' ? [{ value: 'graph', label: 'Graph' }] : []),
                  ]}
                  value={cv}
                  onChange={sv}
                />
              );
            })()}
            {activeTab !== 'staff' && (
              <span className="ml-auto text-xs text-gray-400">{currentItems.length} record{currentItems.length !== 1 ? 's' : ''}</span>
            )}
            </div>
          )}
        >
          <div className="flex flex-wrap items-center gap-2">
            {adminUser && (
              <select value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                <option value="">All Staff</option>
                {historyStaffOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            {activeTab === 'attendance' && (
              <select value={attRecordFilter} onChange={(e) => setAttRecordFilter(e.target.value)} className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" title="Filter record type">
                <option value="all">All Records</option>
                <option value="punch">In / Out</option>
                <option value="leave">Leave</option>
                <option value="holiday">Holiday</option>
                <option value="absentee">Absentee</option>
                <option value="lifecycle">Employment Events</option>
              </select>
            )}
            {activeTab === 'attendance' && (adminUser || hasPermission('staff', 'add') || hasPermission('attendance', 'add')) && (
              <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
                <input type="checkbox" checked={showAllStaff} onChange={e => setShowAllStaff(e.target.checked)} className="w-4 h-4 rounded border-gray-300" />
                Show missing entries
              </label>
            )}
          </div>
        </UiDateRangeBlock>
      )}

      {activeTab === 'salary_schedule' ? (
        <div className="space-y-4">
          {salaryOverviewLoading && (
            <div className="flex items-center gap-2 text-sm text-primary-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <span className="inline-block w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
              Loading salary data…
            </div>
          )}
          {!salaryOverviewLoading && salaryOverviewEventRows.length === 0 && (salaryPeriodRange.from || salaryPeriodRange.to) && (
            <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              No salary activity for {salaryPeriodRange.from || '—'} to {salaryPeriodRange.to || '—'}. Try a different month/range, or hard-refresh (Ctrl+Shift+R) if you expected data here.
            </div>
          )}
          <UiDateRangeBlock
            preset={salaryPreset}
            presetOptions={salaryView === 'calendar'
              ? buildStandardPresetOptions(['month', 'year'])
              : buildStandardPresetOptions(['today', 'week', 'month', 'year', 'all', 'custom'])}
            onPresetChange={applySalaryDatePreset}
            onPrevious={() => shiftSalaryDateRange(-1)}
            onNext={() => shiftSalaryDateRange(1)}
            periodPicker={salaryView === 'calendar'}
            onPeriodChange={handleSalaryCalendarPeriodChange}
            fromDate={salaryFilterRange.from}
            onFromDateChange={handleSalaryStartDateChange}
            toDate={salaryFilterEndCalendarDate}
            onToDateChange={handleSalaryEndDateChange}
            fromTime={salaryStartTime}
            onFromTimeChange={handleSalaryStartTimeChange}
            toTime={salaryEndTime}
            onToTimeChange={handleSalaryEndTimeChange}
            className="border-gray-200"
            rangeText={salarySelectedRangeLabel ? `Selected range: ${salarySelectedRangeLabel}` : ''}
            header={(
              <div className="flex flex-wrap items-center gap-2">
                <UiViewToggle
                  options={[{ value: 'table', label: 'Table' }, { value: 'calendar', label: 'Calendar' }]}
                  value={salaryView}
                  onChange={setSalaryView}
                />
                <span className="ml-auto text-xs text-gray-400">{salaryAggregateRows.length} staff</span>
              </div>
            )}
          >
            <div className="flex flex-wrap items-center gap-2">
              <select value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                <option value="">All Staff</option>
                {salaryStaffOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select value={salaryEntryFilter} onChange={(e) => setSalaryEntryFilter(e.target.value)} className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                <option value="all">All Entries</option>
                <option value="due">Due Salary</option>
                <option value="salary">Salary Paid</option>
                <option value="advance">Advance</option>
                <option value="bonus">Bonus</option>
                <option value="gift">🎁 Gift</option>
                <option value="deduction">Deduction</option>
              </select>
            </div>
            <div className="border-t border-gray-100 pt-3">
              <UiSearchField
                value={salarySearch}
                onChange={(e) => setSalarySearch(e.target.value)}
                placeholder="Search staff..."
                inputClassName="border-gray-200 focus:border-primary-500 focus:ring-primary-100"
              />
            </div>
          </UiDateRangeBlock>
          <UiPanel className="border-gray-200" bodyClassName="pt-2 space-y-2 p-4">
            <div className="pt-2 border-t border-gray-100 space-y-2">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-primary-700 font-semibold">Grand Total</p>
                    <p className="text-sm font-semibold text-gray-800">{salaryAggregateGrandTotal.count} staff</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Payroll position as of {formatDate(effectiveSalaryAsOfDate)}</p>
                    <p className="text-sm font-bold text-primary-700">{formatSalaryCurrency(salaryAggregateGrandTotal.totalUnpaidAsOfToday)}</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                  <div className="rounded-lg border border-white/70 bg-white/70 p-2">
                    <p className="text-xs font-semibold text-gray-700">Over / Underpayment Carried Forward</p>
                    <p className={`text-sm font-bold ${salaryAggregateGrandTotal.totalSignedCarryForward >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{formatSignedCarry(salaryAggregateGrandTotal.totalSignedCarryForward, formatSalaryCurrency)}</p>
                  </div>
                  <div className="rounded-lg border border-white/70 bg-white/70 p-2">
                    <p className="text-xs font-semibold text-gray-700">Advance in Cycle</p>
                    <p className="text-sm font-bold text-emerald-700">{formatSalaryCurrency(salaryAggregateGrandTotal.totalAdvance)}</p>
                  </div>
                  <div className="rounded-lg border border-white/70 bg-white/70 p-2">
                    <p className="text-xs font-semibold text-gray-700">Deductions in Cycle</p>
                    <p className="text-sm font-bold text-rose-700">{formatSalaryCurrency(salaryAggregateGrandTotal.totalDeductions)}</p>
                  </div>
                  <div className="rounded-lg border border-white/70 bg-white/70 p-2">
                    <p className="text-xs font-semibold text-gray-700">Bonus in Cycle</p>
                    <p className="text-sm font-bold text-emerald-700">{formatSalaryCurrency(salaryAggregateGrandTotal.totalBonus)}</p>
                  </div>
                  <div className="rounded-lg border border-white/70 bg-white/70 p-2">
                    <p className="text-xs font-semibold text-gray-700">Gift in Cycle</p>
                    <p className="text-sm font-bold text-emerald-700">{formatSalaryCurrency(salaryAggregateGrandTotal.totalGift)}</p>
                  </div>
                </div>
              </div>

              {salaryAggregateRows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 p-4 text-center text-sm text-gray-400">No staff salary summary for this selection.</div>
              ) : (
                salaryAggregateRows.map((row) => (
                  <div key={`salary-summary-${row.staffId}`} className="border border-gray-100 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleSalarySummaryStaff(row.staffId)}
                      className="w-full px-3 py-3 bg-gray-50 hover:bg-gray-100 grid grid-cols-[minmax(0,1fr)_11rem] items-center gap-3 text-left transition"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        {collapsedSalarySummaryStaff.has(row.staffId) ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        <span className="min-w-0">
                          <span className={`block text-sm font-semibold truncate ${getWageTypeClass(row.wageType)}`} title={getWageTypeLabel(row.wageType)}>{row.staffName}</span>
                          <span className="block text-xs text-gray-500 truncate">{row.department || 'No department'} · {getWageTypeLabel(row.wageType)}</span>
                        </span>
                      </span>
                      <span className="text-right">
                        <span className="block text-xs text-gray-500">
                          {row.cycleDisplayState === 'current' ? 'Accrued as of' : 'Outstanding as of'} {formatDate(row.snapshotDate || effectiveSalaryAsOfDate)}
                        </span>
                        <span className="block text-sm font-bold text-primary-700">{formatSalaryCurrency(row.runningSalaryBalance ?? row.unpaidAsOfToday)}</span>
                      </span>
                    </button>
                    {!collapsedSalarySummaryStaff.has(row.staffId) && (
                      <SalaryCycleSummary
                        summary={row}
                        breakdown={row.breakdown || {}}
                        deductionRecords={advances.filter((record) => (
                          record.payment_type === 'deduction'
                          && String(record.staff?.id ?? record.staff) === String(row.staffId)
                        ))}
                        asOfDate={row.snapshotDate || effectiveSalaryAsOfDate}
                        formatCurrency={formatSalaryCurrency}
                        formatDate={formatDate}
                      />
                    )}
                    {false && !collapsedSalarySummaryStaff.has(row.staffId) && (
                      <div className="divide-y divide-gray-100 bg-white">
                        {(() => {
                          const b = row.breakdown || {};
                          const isDaily = (b.wageType || row.wageType) === 'daily_wages';
                          // As-of the effective date: TODAY for a running month, the month's last
                          // day for a past month (not the calendar range-end, which overshoots).
                          const asOnLabel = formatDate(effectiveSalaryAsOfDate);
                          const cycleLabel = row.cycleStart && row.cycleEnd ? `${formatDate(row.cycleStart)} to ${formatDate(row.cycleEnd)}` : '-';
                          const items = isDaily ? [
                            { header: 'a. Wages (daily-wage)', note: cycleLabel },
                            { label: 'Daily wage rate', value: b.dailyRate, tone: 'text-gray-700' },
                            { label: 'Days present (in + out punch)', text: `${b.daysPresent ?? 0} day(s)` },
                            { label: 'Total wage earned (days × rate)', value: b.grossEarned, tone: 'text-emerald-700', bold: true },
                          ] : [
                            { header: 'a. Salary (monthly)', note: cycleLabel },
                            { label: 'Monthly salary', value: b.dailyRate ? b.dailyRate * 30 : 0, tone: 'text-gray-700' },
                            { label: 'Daily pro-rata rate (÷ 30)', value: b.dailyRate, tone: 'text-gray-500' },
                            { label: 'Days present (attendance)', text: `${b.daysPresent ?? 0} present · ${b.paidLeaveDays ?? 0} paid-leave` },
                            { label: 'Salary earned this cycle', value: b.grossEarned, tone: 'text-emerald-700', bold: true },
                          ];
                          items.push(
                            { header: 'b. Leaves & Off-days' },
                            { label: 'Holidays', text: `${b.holidays ?? 0} day(s)` },
                            { label: 'Paid leaves', text: `${b.paidLeaves ?? 0} day(s)` },
                            { label: 'Unpaid leaves', text: `${b.unpaidLeaves ?? 0} day(s)` },
                            { label: 'Waived absentee', text: `${b.waivedAbsentee ?? 0} day(s)` },
                            { label: 'Deducted absentee', text: `${b.deductedAbsentee ?? 0} day(s)` },
                            { header: 'c. Deductions' },
                            { label: 'Un-approved leave violation', value: b.deductionLeaveViolation, tone: 'text-rose-700' },
                            { label: 'Performance violation', value: b.deductionPerformance, tone: 'text-rose-700' },
                            { label: 'Total deductions', value: row.totalDeductions, tone: 'text-rose-700', bold: true },
                            { header: 'd. Advances' },
                            { label: 'Total advances in cycle', value: row.totalAdvance, tone: 'text-emerald-700' },
                            { header: 'e. Bonus / Gift' },
                            { label: 'Total bonus in cycle', value: row.totalBonus, tone: 'text-emerald-700' },
                            { label: 'Total gift in cycle', value: row.totalGift, tone: 'text-emerald-700' },
                            { header: 'f. Payable Salary (a - c - d)' },
                            { label: 'Payable salary before carry', value: row.payableSalaryBeforeCarry, tone: 'text-primary-700', bold: true },
                            { header: 'g. Over / Underpayment Carried Forward' },
                            { label: 'Positive = overpayment; negative = underpayment', text: formatSignedCarry(row.signedCarryForward, formatSalaryCurrency), tone: Number(row.signedCarryForward || 0) >= 0 ? 'text-emerald-700' : 'text-amber-700', bold: true },
                            { header: 'h. Total Payable (f - g)' },
                            { label: `Total payable as on ${asOnLabel}`, value: row.unpaidAsOfToday, tone: 'text-primary-700', bold: true },
                          );
                          return items.map((it, idx) => (
                            it.header ? (
                              <div key={`${row.staffId}-h-${idx}`} className="px-4 py-1.5 bg-gray-50/70 flex items-center justify-between">
                                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{it.header}</span>
                                {it.note ? <span className="text-[10px] text-gray-400">{it.note}</span> : null}
                              </div>
                            ) : (
                              <div key={`${row.staffId}-${idx}`} className="px-9 py-2 grid grid-cols-[minmax(0,1fr)_11rem] items-center gap-3 text-left">
                                <span className="text-xs sm:text-sm text-gray-600">{it.label}</span>
                                <span className={`text-xs sm:text-sm whitespace-nowrap text-right ${it.bold ? 'font-bold' : 'font-semibold'} ${it.tone || 'text-gray-700'}`}>
                                  {it.text != null ? it.text : formatSalaryCurrency(it.value)}
                                </span>
                              </div>
                            )
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </UiPanel>

          {/* Table View */}
          {salaryView === 'table' && (
            sortedSalaryEvents.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-sm text-gray-400">No salary records in this period</div>
            ) : (
              <div className="bg-white rounded-xl border overflow-x-auto">
                <table className="w-full text-sm" style={{minWidth:'860px'}}>
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <SortableHeader label="Date" col="date" sortKey={salarySortKey} sortDir={salarySortDir} onSort={salaryHandleSort} />
                      <SortableHeader label="Staff" col="staff_name" sortKey={salarySortKey} sortDir={salarySortDir} onSort={salaryHandleSort} />
                      <SortableHeader label="Type" col="row_type" sortKey={salarySortKey} sortDir={salarySortDir} onSort={salaryHandleSort} />
                      <SortableHeader label="Amount" col="amount" sortKey={salarySortKey} sortDir={salarySortDir} onSort={salaryHandleSort} right />
                      <SortableHeader label="Remaining Due" col="remaining_due" sortKey={salarySortKey} sortDir={salarySortDir} onSort={salaryHandleSort} right />
                      <SortableHeader label="Status" col="status" sortKey={salarySortKey} sortDir={salarySortDir} onSort={salaryHandleSort} />
                      <SortableHeader label="Reason / Notes" col="note" sortKey={salarySortKey} sortDir={salarySortDir} onSort={salaryHandleSort} />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sortedSalaryEvents.map((row) => {
                      const isExpanded = expandedDueRowId === row.id;
                      return (
                      <React.Fragment key={row.id}>
                      <tr className={`hover:bg-gray-50 ${row.rowType === 'deduction' && row.performanceId ? 'cursor-pointer' : ''}`} onClick={row.rowType === 'deduction' && row.performanceId ? () => openPerformanceRecord(row) : undefined} title={row.rowType === 'deduction' && row.performanceId ? 'Open in Performance tab' : undefined}>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(row.date)}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">{row.staffName}</div>
                          <div className="text-xs text-gray-400">{row.department || ''}</div>
                        </td>
                        <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${getSalaryEntryBadgeClass(row.rowType)}`}>{getSalaryEntryLabel(row.rowType, row)}</span>{row.rowType === 'deduction' && row.performanceId && <span className="ml-1 text-[10px] text-primary-500 underline">view</span>}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${row.rowType === 'deduction' ? 'text-rose-700' : row.rowType === 'due' ? 'text-primary-700' : 'text-gray-800'}`}>{row.rowType === 'lifecycle' ? <span className="text-gray-300">—</span> : formatSalaryCurrency(row.amount)}</td>
                        <td className="px-4 py-3 text-right">
                          {row.remainingDue > 0 ? (
                            <button
                              type="button"
                              onClick={() => setExpandedDueRowId(isExpanded ? null : row.id)}
                              className="font-bold text-green-700 hover:underline inline-flex items-center gap-1"
                              title="Show how this is computed"
                            >
                              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              {formatSalaryCurrency(row.remainingDue)}
                            </button>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 capitalize">{row.status || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{row.note || '—'}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50/60">
                          <td colSpan={7} className="px-8 py-2">
                            {(() => {
                              const t = row.dueTrace;
                              if (!t) return <p className="text-xs text-gray-400">No breakdown available.</p>;
                              const List = ({ title, items, sign, tone }) => (items && items.length ? (
                                <>
                                  <div className="px-3 pt-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">{title}</div>
                                  {items.map((it, i) => (
                                    <div key={i} className="flex items-center justify-between px-3 py-0.5 text-xs">
                                      <span className="text-gray-500 truncate">{formatDate(it.date)}{it.reason ? ` · ${it.reason}` : ''}</span>
                                      <span className={`font-semibold shrink-0 ${tone}`}>{sign}{formatSalaryCurrency(it.amount)}</span>
                                    </div>
                                  ))}
                                </>
                              ) : null);
                              return (
                                <div className="border rounded-lg divide-y divide-gray-100 bg-white max-w-lg">
                                  <div className="flex items-center justify-between px-3 py-1 text-xs"><span className="text-gray-500">{Number(t.carriedForward) < 0 ? 'Overpayment carried forward' : 'Unpaid carried forward'}</span><span className={`font-semibold ${Number(t.carriedForward) < 0 ? 'text-emerald-600' : 'text-gray-800'}`}>{formatSalaryCurrency(t.carriedForward)}</span></div>
                                  <div className="flex items-center justify-between px-3 py-1 text-xs"><span className="text-gray-500">Salary/wage earned (to {formatDate(t.asOfDate)})</span><span className="font-semibold text-emerald-700">+{formatSalaryCurrency(t.cycleEarned)}</span></div>
                                  <div><List title="Salary payments" items={t.salaryPayments} sign="−" tone="text-green-700" /></div>
                                  <div><List title="Advances" items={t.advances} sign="−" tone="text-green-700" /></div>
                                  <div><List title="Deductions" items={t.deductions} sign="−" tone="text-rose-700" /></div>
                                  {t.bonuses && t.bonuses.length > 0 && <div><List title="Bonus" items={t.bonuses} sign="+" tone="text-green-700" /></div>}
                                  {t.gifts && t.gifts.length > 0 && <div><List title="Gift" items={t.gifts} sign="+" tone="text-green-700" /></div>}
                                  <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50"><span className="font-bold text-gray-700">Total remaining due (as on {formatDate(t.asOfDate)})</span><span className="font-bold text-primary-700">{formatSalaryCurrency(t.total)}</span></div>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t">
                    <tr>
                      <td colSpan={3} className="px-4 py-2.5 text-xs text-gray-500 font-medium">Total ({sortedSalaryEvents.length} entries)</td>
                      <td className="px-4 py-2.5 text-right text-xs font-bold text-gray-700">{formatSalaryCurrency(sortedSalaryEvents.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0))}</td>
                      <td className="px-4 py-2.5 text-right text-xs font-bold text-green-700">{formatSalaryCurrency(sortedSalaryEvents.reduce((sum, row) => sum + parseFloat(row.remainingDue || 0), 0))}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          )}
          {false && salaryView === 'table' && (
            filteredSalarySchedule.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-sm text-gray-400">No salary due in this period</div>
            ) : (
              <div className="bg-white rounded-xl border overflow-x-auto">
                <table className="w-full text-sm" style={{minWidth:'720px'}}>
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Staff</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600">Salary Day</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Next Due</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Monthly Salary</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Advance Taken</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Total Due</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredSalarySchedule.map(s => (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className={`font-semibold ${getWageTypeClass(s.wage_type)}`} title={getWageTypeLabel(s.wage_type)}>{s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim()}</div>
                          <div className="text-xs text-gray-400">{s.department_name || s.designation || ''}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-semibold">{s._sDay}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(s._dueDate)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-800">{formatSalaryCurrency(s.monthly_salary)}</td>
                        <td className="px-4 py-3 text-right text-red-600">{s._outstanding > 0 ? formatSalaryCurrency(s._outstanding) : <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3 text-right font-bold text-green-700">{formatSalaryCurrency(s._netPayable)}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => startEdit(s)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit Staff"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(s.id, s)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Delete Staff"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t">
                    <tr>
                      <td colSpan={3} className="px-4 py-2.5 text-xs text-gray-500 font-medium">Total ({filteredSalarySchedule.length} staff)</td>
                      <td className="px-4 py-2.5 text-right text-xs font-bold text-gray-700">{formatSalaryCurrency(filteredSalarySchedule.reduce((s, r) => s + parseFloat(r.monthly_salary || 0), 0))}</td>
                      <td className="px-4 py-2.5 text-right text-xs font-bold text-red-600">{formatSalaryCurrency(filteredSalarySchedule.reduce((s, r) => s + r._outstanding, 0))}</td>
                      <td className="px-4 py-2.5 text-right text-xs font-bold text-green-700">{formatSalaryCurrency(filteredSalarySchedule.reduce((s, r) => s + r._netPayable, 0))}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          )}

          {/* Calendar View - Month or Year */}
          {salaryView === 'calendar' && (() => {
            const MO_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            const MO_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const todayStr = new Date().toISOString().substring(0, 10);
            const calYr = salaryCalMonth.y;
            
            /* ── Year Grid View ─────────────────────────────────────── */
            if (salaryPreset === 'year') {
              const monthAgg = buildSalaryMonthDueTotals(filteredSalaryEvents);
              return (
                <div className="bg-white rounded-xl border p-4">
                  <div className="flex items-center justify-between mb-4">
                    <button onClick={() => setSalaryCalMonth(m => {
                      const nextYear = m.y - 1;
                      setSalaryRangeOverride({ from: `${nextYear}-01-01`, to: `${nextYear}-12-31` });
                      setSalaryPreset('year');
                      return { y: nextYear, m: m.m };
                    })} className="p-1.5 hover:bg-gray-100 rounded-lg"><ChevronRight size={16} className="rotate-180" /></button>
                    <span className="text-sm font-semibold text-gray-700">{calYr} — Salary Overview</span>
                    <button onClick={() => setSalaryCalMonth(m => {
                      const nextYear = m.y + 1;
                      setSalaryRangeOverride({ from: `${nextYear}-01-01`, to: `${nextYear}-12-31` });
                      setSalaryPreset('year');
                      return { y: nextYear, m: m.m };
                    })} className="p-1.5 hover:bg-gray-100 rounded-lg"><ChevronRight size={16} /></button>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {MO_SHORT.map((mName, mi) => {
                      const ms = `${calYr}-${String(mi+1).padStart(2,'0')}`;
                      const agg = monthAgg[ms] || { count: 0, totalNet: 0 };
                      const isFutureMonth = ms > todayStr.substring(0,7);
                      let bg = 'bg-gray-50 border-gray-200', txt = 'text-gray-400';
                      if (!isFutureMonth && agg.count > 0) {
                        bg = 'bg-gray-50 border-gray-200'; txt = 'text-primary-800';
                      } else if (!isFutureMonth) {
                        bg = 'bg-gray-300 border-gray-400'; txt = 'text-gray-600';
                      }
                      return (
                        <div key={ms} onClick={() => {
                          if (!isFutureMonth && agg.count > 0) {
                            const firstDay = `${calYr}-${String(mi + 1).padStart(2, '0')}-01`;
                            const lastDay = `${calYr}-${String(mi + 1).padStart(2, '0')}-${String(new Date(calYr, mi + 1, 0).getDate()).padStart(2, '0')}`;
                            setSalaryRangeOverride({ from: firstDay, to: lastDay });
                            setSalaryPreset('month');
                            setSalaryCalMonth({ y: calYr, m: mi + 1 });
                          }
                        }}
                          className={`${bg} border rounded-xl p-3 ${!isFutureMonth && agg.count > 0 ? 'cursor-pointer hover:shadow-md transition' : 'opacity-40'}`}>
                          <div className={`font-bold text-sm ${txt}`}>{mName}</div>
                          {!isFutureMonth && agg.count > 0 && (
                            <div className="mt-1 space-y-0.5">
                              <div className="text-xs text-primary-700">{agg.count} staff</div>
                              <div className="text-xs text-primary-600 font-semibold">Rs.{(agg.totalNet/1000).toFixed(1)}K due</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            
            /* ── Month Calendar View ────────────────────────────────── */
            const firstDow = new Date(salaryCalMonth.y, salaryCalMonth.m - 1, 1).getDay();
            const firstMon = firstDow === 0 ? 6 : firstDow - 1;
            const daysInMonth = new Date(salaryCalMonth.y, salaryCalMonth.m, 0).getDate();
            const cells = Array(firstMon).fill(null);
            for (let d = 1; d <= daysInMonth; d++) cells.push(d);
            while (cells.length % 7 !== 0) cells.push(null);
            return (
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center justify-between mb-4">
                  <button onClick={() => setSalaryCalMonth(m => {
                    const d = new Date(m.y, m.m - 2, 1);
                    const firstDay = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
                    const lastDay = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
                    setSalaryRangeOverride({ from: firstDay, to: lastDay });
                    setSalaryPreset('month');
                    return { y: d.getFullYear(), m: d.getMonth() + 1 };
                  })} className="p-1.5 hover:bg-gray-100 rounded-lg"><ChevronRight size={16} className="rotate-180" /></button>
                  <span className="text-sm font-semibold text-gray-700">{MO_NAMES[salaryCalMonth.m - 1]} {salaryCalMonth.y}</span>
                  <button onClick={() => setSalaryCalMonth(m => {
                    const d = new Date(m.y, m.m, 1);
                    const firstDay = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
                    const lastDay = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
                    setSalaryRangeOverride({ from: firstDay, to: lastDay });
                    setSalaryPreset('month');
                    return { y: d.getFullYear(), m: d.getMonth() + 1 };
                  })} className="p-1.5 hover:bg-gray-100 rounded-lg"><ChevronRight size={16} /></button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center mb-1">
                  {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => <div key={d} className="text-xs text-gray-400 font-medium py-1">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((day, idx) => {
                    if (!day) return <div key={idx} />;
                    const ds = `${salaryCalMonth.y}-${String(salaryCalMonth.m).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const info = salaryCalData[ds];
                    const holiday = getHolidayForDate(ds);
                    const isToday = ds === todayStr;
                    const isPast = ds < todayStr;
                    const isFuture = ds > todayStr;
                    const hasPaid = (info?.paidTotal || 0) > 0;
                    const hasAdvance = (info?.advanceTotal || 0) > 0;
                    const hasBonus = (info?.bonusTotal || 0) > 0;
                    const hasGift = (info?.giftTotal || 0) > 0;
                    const hasDeduction = (info?.deductionTotal || 0) > 0;
                    // Show the payday "?" marker whenever a due row exists — even when the balance
                    // is 0 (renders "0?") — so every payday is marked, not just those still owed.
                    const hasDue = (info?.dueEntries?.length || 0) > 0;
                    let cellClass = 'bg-gray-50 border-gray-100';
                    if (hasPaid) cellClass = 'bg-green-50 border-green-300';
                    else if (hasBonus || hasGift || hasAdvance) cellClass = 'bg-green-50 border-green-300';
                    else if (hasDeduction) cellClass = 'bg-rose-50 border-rose-300';
                    else if (hasDue && isFuture) cellClass = 'bg-gray-300 border-gray-400 opacity-70';
                    else if (hasDue) cellClass = 'bg-gray-50 border-gray-300';
                    else if (isPast) cellClass = 'bg-gray-300 border-gray-400';
                    else if (isToday) cellClass = 'bg-blue-50 border-blue-200';
                    if (holiday && !hasPaid && !hasAdvance && !hasBonus && !hasGift && !hasDeduction && !hasDue) cellClass = 'bg-amber-50 border-amber-300';
                    return (
                      <div key={idx} className={`rounded-lg p-1.5 min-h-[74px] border ${cellClass} ${(hasPaid || hasAdvance || hasBonus || hasGift || hasDeduction || hasDue) ? 'cursor-pointer hover:opacity-80' : ''} ${holiday ? 'ring-1 ring-amber-300' : ''} ${isToday ? 'ring-2 ring-blue-400' : ''}`}
                        onClick={() => {
                          if (isFuture && hasDue) {
                            setSalaryRangeOverride({ from: ds, to: ds });
                            setSalaryPreset('custom');
                            setSalaryCustomFrom(ds);
                            setSalaryCustomTo(ds);
                            setSalaryEntryFilter('due');
                            setSalaryView('table');
                            return;
                          }
                          if (isPast && (hasPaid || hasAdvance || hasBonus || hasGift || hasDeduction || hasDue)) {
                            setSalaryRangeOverride({ from: ds, to: ds });
                            setSalaryPreset('custom');
                            setSalaryCustomFrom(ds);
                            setSalaryCustomTo(ds);
                            setSalaryEntryFilter(
                              hasDeduction && !hasPaid && !hasAdvance && !hasBonus && !hasGift && !hasDue
                                ? 'deduction'
                                : 'all'
                            );
                            setSalaryView('table');
                          }
                        }}>
                        <div className="text-xs font-bold mb-0.5 text-gray-700">{day}</div>
                        {holiday && <div className="text-[9px] text-amber-700 font-semibold leading-tight truncate">{holiday.name}</div>}
                        <div className="space-y-0.5">
                          {hasPaid && renderSalaryCalendarLines(info.paidEntries, 'text-green-700', '✓', 2, true)}
                          {hasBonus && renderSalaryCalendarLines(info.bonusEntries, 'text-green-700', '★', 2, true)}
                          {hasGift && renderSalaryCalendarLines(info.giftEntries, 'text-green-700', '🎁', 2, true)}
                          {hasAdvance && renderSalaryCalendarLines(info.advanceEntries, 'text-green-700', '+', 2, true)}
                          {hasDeduction && renderSalaryCalendarLines(info.deductionEntries, 'text-rose-700', 'X', 2, true)}
                          {hasDue && renderSalaryCalendarLines(info.dueEntries, isFuture ? 'text-gray-600' : 'text-primary-700', '?')}
                          {(() => {
                            const offs = Object.keys(offDayMap)
                              .filter(k => k.endsWith(`_${ds}`) && (!staffFilter || k.startsWith(`${staffFilter}_`)))
                              .map(k => offDayMap[k]);
                            const evs = Object.keys(lifecycleMap)
                              .filter(k => k.endsWith(`_${ds}`) && (!staffFilter || k.startsWith(`${staffFilter}_`)))
                              .flatMap(k => lifecycleMap[k]);
                            return (<>
                              {offs.map((r, i) => <LifecycleBadge key={`off-${i}`} type={r.violation_type} name={getShortStaffName(r.staff_name)} filled={offDayFilled(r.violation_type, r.is_warning)} size={13} />)}
                              {evs.map((ev, i) => <LifecycleBadge key={`life-${i}`} type={ev.event_type} name={getShortStaffName(ev.staff_name)} size={13} />)}
                            </>);
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> <span className="text-green-700 font-semibold">✓</span> Paid</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> <span className="text-green-700 font-semibold">★</span> Bonus</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> <span className="text-green-700 font-semibold">🎁</span> Gift</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> <span className="text-green-700 font-semibold">+</span> Advance</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-50 border border-rose-300" /> <span className="text-rose-700 font-semibold">X</span> Deduction</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-50 border border-gray-300" /> <span className="text-primary-700 font-semibold">?</span> Due</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-300" /> Holiday</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-300 border border-gray-400 opacity-70" /> <span className="text-gray-600 font-semibold">?</span> Future Date</span>
                  <EventLegend />
                </div>
              </div>
            );
          })()}
        </div>
      ) : activeTab === 'shop_hours' ? (
        <ShopHoursTab
          shopHours={shopHours}
          publicHolidays={publicHolidays}
          hasPermission={hasPermission}
          adminUser={adminUser}
          onDataChanged={fetchAll}
        />
      ) : activeTab === 'staff' ? (
        <StaffTab
          staff={staff}
          departments={departments}
          users={users}
          hasPermission={hasPermission}
          onDataChanged={fetchAll}
        />
      ) : activeTab === 'roster' ? (
        <RosterTab
          rosters={rosters}
          staff={staff}
          shopHours={shopHours}
          leaveEntries={leaveEntries}
          publicHolidays={publicHolidays}
          currentBusinessDate={currentBusinessDate}
          canManage={adminUser}
          onDataChanged={() => fetchAll(true)}
        />
      ) : (canViewStaff || canViewAttendance || canViewLeaves) ? (
        <>

          {activeTab !== 'performance' && activeTab !== 'staff_dashboard' && (
          <UiPanel bodyClassName="p-4">
            <UiSearchField value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${tabs.find((tab) => tab.id === activeTab)?.label || activeTab}...`} />
          </UiPanel>
          )}
          {(() => {
            if (activeTab === 'performance' || activeTab === 'staff_dashboard') return null;
            const currentView = activeTab === 'attendance' ? attView : activeTab === 'leave' ? leaveView : activeTab === 'roster' ? rosterView : activeTab === 'deductions' ? deductionsView : activeTab === 'advances' ? advancesView : 'table';
            const todayStr = new Date().toISOString().substring(0, 10);
            // Attendance "Graph" view — reuses the AttendanceChart timeline for each staff.
            if (activeTab === 'attendance' && currentView === 'graph') {
              const gMonth = String(startDate || '').slice(0, 7) || todayStr.slice(0, 7);
              const gStaffSource = staffFilter
                ? staff.filter((s) => String(s.id) === String(staffFilter))
                : staff.filter((s) => s.employment_status !== 'terminated');
              const gStaff = gStaffSource.map((s) => ({
                id: s.id,
                name: `${(s.full_name || `${s.first_name || ''} ${s.last_name || ''}`).trim()}${s.wage_type === 'daily_wages' ? ' · Daily' : ''}`,
              }));
              return <AttendanceGraphView staffList={gStaff} month={gMonth} />;
            }
            const calCtx = { activeTab, advances, attendance, businessEndTime, businessStartTime, calMonthYM, calendarData, changeCalMonth, datePreset, drillToMonth, getHolidayForDate, setActiveTab, setAdvancesView, setAttView, setDatePreset, setDeductionsView, setEndDate, setEndTime, setLeaveView, setRosterView, setSalaryView, setStaffCustomRange, setStartDate, setStartTime, startDate, todayStr, yearCalData };
            if (currentView === 'calendar') {
              return renderStaffRecordsCalendar(calCtx);
            }
            const tableCtx = { activeTab, offDayMap, rosterMap, leaveMap, firstPunchInSet, attendance, businessDayStartHour, getArrivalStatus, getBusinessDateForDateTimeValue, selectedPunches, togglePunchSelect, getWageTypeClass, getWageTypeLabel, fmtDate, fmtDateTime, formatDate, formatUiCurrencyCompact, DAY_NAMES, shopHoursListTab, adminUser, hasPermission, canManageLeaveViolations, setOffDayEditRecord, handleDeleteLeave, handleDeleteOffDay, openFillPlaceholder, handleToggleLeaveViolation, startEdit, loadStaffEvents, handleDelete, groupedByDate, displayItems, currentItems, allDates, collapsedDates, setCollapsedDates, setPunchSelection, bulkDeletePunches, smSortKey, smSortDir, smHandleSort, getHolidayForDate, toggleDate, getShopHoursLabel };
            return renderStaffRecordsTable(tableCtx);
          })()}
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
          <p className="text-base font-medium mb-1">
            Use the "Add Attendance" button to record your punch in/out.
          </p>
          <p className="text-sm text-gray-400">Historical records are visible to admins only.</p>
        </div>
      )}

      {activeTab === 'staff' && selectedStaffEventsStaff && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  Events: {selectedStaffEventsStaff.full_name || `${selectedStaffEventsStaff.first_name || ''} ${selectedStaffEventsStaff.last_name || ''}`.trim()}
                </p>
                <p className="text-xs text-gray-500">Manage dynamic staff history, salary updates, lifecycle changes, and financial events here.</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openStaffEventCreate()} className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                  Add Event
                </button>
                <button onClick={() => setSelectedStaffEventsStaff(null)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {staffEventsLoading ? (
                <div className="p-6 text-sm text-gray-500">Loading events...</div>
              ) : selectedStaffEvents.length === 0 ? (
                <div className="p-6 text-sm text-gray-500">No events found for this staff member.</div>
              ) : (
                <div className="divide-y">
                  {selectedStaffEvents.map((eventItem) => (
                    <div key={eventItem.key} className="px-4 py-3 flex items-start justify-between gap-3 hover:bg-gray-50">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            eventItem.source === 'lifecycle'
                              ? 'bg-slate-100 text-slate-700'
                              : eventItem.event_type === 'deduction'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-green-100 text-green-700'
                          }`}>
                            {eventItem.event_type_label}
                          </span>
                          <span className="text-sm font-medium text-gray-700">{formatDate(eventItem.event_date)}</span>
                          {eventItem.amount != null && (
                            <span className="text-sm font-semibold text-gray-800">{formatUiCurrencyCompact(eventItem.amount)}</span>
                          )}
                        {eventItem.summary && eventItem.summary !== eventItem.reason ? <span className="text-xs text-gray-500">{eventItem.summary}</span> : null}
                      </div>
                        {eventItem.reason ? <p className="text-xs text-gray-500 mt-1">{eventItem.reason}</p> : null}
                        {eventItem.status && <p className="text-[11px] text-gray-400 mt-1 uppercase">{eventItem.status}</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openStaffEventEdit(eventItem)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit Event">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDeleteStaffEvent(eventItem)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete Event">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'performance' && (
        <StaffPerformance
          addViolationTrigger={perfAddTrigger}
          penaltyTrigger={perfPenaltyTrigger}
          onPayrollChanged={() => fetchAll(true)}
          staffFilter={performanceStaffFilter}
          onStaffFilterChange={setPerformanceStaffFilter}
          focusRecord={perfFocusRecord}
        />
      )}

      {activeTab === 'staff_dashboard' && (
        <StaffDashboardTab staff={staff} dashboardStaffId={dashboardStaffId} setDashboardStaffId={setDashboardStaffId} />
      )}

      <SalaryHelpModal open={showSalaryHelp} onClose={() => setShowSalaryHelp(false)} />




      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">{editingItem ? 'Edit' : 'Add'} {activeTab === 'advances' ? 'Payment' : activeTab === 'deductions' ? 'Deduction' : activeTab === 'shop_hours' ? 'Shop Hours' : tabs.find((tab) => tab.id === activeTab)?.label}</h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {renderModalForm()}
              <div className="flex justify-end gap-2 pt-2 border-t">
                {((activeTab === 'attendance' && pendingAttendanceEntries.length > 0) || (activeTab === 'leave' && pendingLeaveEntries.length > 0)) ? (
                  <>
                    <button type="button" onClick={closeModal} className="px-4 py-2 border border-gray-200 rounded-lg text-sm">Cancel</button>
                    <button type="button" onClick={activeTab === 'attendance' ? handleSaveAllAttendance : handleSaveAllLeave} disabled={(activeTab === 'attendance' ? attSaving : leaveSaving)} className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
                      {(activeTab === 'attendance' ? attSaving : leaveSaving) ? <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full inline-block" /> : null}
                      Save All ({activeTab === 'attendance' ? pendingAttendanceEntries.length : pendingLeaveEntries.length})
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={closeModal} className="px-4 py-2 border rounded-lg">Cancel</button>
                    <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {(showAddLeave || offDayEditRecord) && (
        <AddLeaveModal
          staff={staff}
          defaultDate={currentBusinessDate}
          editRecord={offDayEditRecord}
          onClose={() => { setShowAddLeave(false); setEditingLeave(null); setOffDayEditRecord(null); }}
          onSaved={fetchAll}
        />
      )}


      {showSmartAttModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div ref={smartAttModalRef} style={smartAttModalSize.width ? { width: smartAttModalSize.width, height: smartAttModalSize.height, maxWidth: '98vw', maxHeight: '98vh' } : {}} className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col relative shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" /><h3 className="text-lg font-bold text-gray-800">Smart Attendance Paste</h3></div>
              <button onClick={() => setShowSmartAttModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            {smartAttStep === 'input' ? (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <p className="text-sm text-gray-500">Paste attendance & off-days — one per line. Start with a date header. Format: <span className="font-mono bg-gray-100 px-1 rounded">Name - In/Out/Leave/Absentee/Holiday - Time - Reason - Notes</span></p>
                <p className="text-xs text-gray-400">In/Out create punches. Leave / Absentee / Holiday create off-day records (no time needed). Anything after the type folds into the record's reason/notes. Leave & Holiday default to Warning; Absentee defaults to Deduct — adjust per row on the next screen.</p>
                <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 font-mono space-y-1">
                  <div>13 April 2026</div>
                  <div>Raees - In - 1400 - Opened the shop</div>
                  <div>Ammar - Out - 2100 - Closed the Shop</div>
                  <div>Sayam - Leave - Sick - had a fever</div>
                  <div>Bilal - Absentee - No show</div>
                  <div>Usman - Holiday - Eid</div>
                </div>
                <textarea className="w-full border rounded-xl p-3 text-sm font-mono focus:ring-2 focus:ring-purple-400 resize-none" rows={10}
                  placeholder="Paste attendance here..." value={smartAttText} onChange={e => setSmartAttText(e.target.value)} autoFocus />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowSmartAttModal(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
                  <button onClick={handleSmartAttParse} disabled={!smartAttText.trim()} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
                    Parse & Preview
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-auto p-4 space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-sm text-gray-600 flex-1 min-w-[200px]">Review and edit before saving. <button className="text-purple-600 underline" onClick={() => setSmartAttStep('input')}>← Back to edit</button></p>
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Set all dates to:</label>
                  <UiDateField value={smartAttDate} onChange={(val) => { setSmartAttDate(val); setSmartAttRows(prev => prev.map(r => ({ ...r, date: val }))); }} className="min-w-[15rem]" inputClassName="focus:border-purple-500 focus:ring-purple-100" />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead><tr className="bg-gray-50 text-left">
                      <th className="px-3 py-2 border font-medium text-gray-600">Staff</th>
                      <th className="px-3 py-2 border font-medium text-gray-600">Date</th>
                      <th className="px-3 py-2 border font-medium text-gray-600">Type</th>
                      <th className="px-3 py-2 border font-medium text-gray-600">Time</th>
                      <th className="px-3 py-2 border font-medium text-gray-600">Status</th>
                      <th className="px-3 py-2 border font-medium text-gray-600">Notes</th>
                      <th className="px-3 py-2 border"></th>
                    </tr></thead>
                    <tbody>
                      {smartAttRows.map((row, i) => {
                        const isOff = row.kind === 'offday';
                        const punchDT = `${row.date}T${row.time}:00`;
                        const shiftStart = rosterMap[`${row.staffId}_${row.date}`];
                        const status = (!isOff && row.punch_type === 'in') ? getArrivalStatus(punchDT, shiftStart) : null;
                        return (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-2 py-1 border min-w-[170px]">
                              <select className="w-full px-1 py-1 border rounded text-xs" value={row.staffId}
                                onChange={e => setSmartAttRows(prev => prev.map((r, j) => j===i ? {...r, staffId: e.target.value} : r))}>
                                <option value="">— Select —</option>
                                {smartAttendanceStaffOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                              {!row.staffId && <p className="text-xs text-amber-600 mt-0.5">⚠ "{row.staffName}"</p>}
                            </td>
                            <td className="px-2 py-1 border">
                              <UiDateField value={row.date || ''}
                                onChange={value => setSmartAttRows(prev => prev.map((r, j) => j===i ? {...r, date: value} : r))} />
                            </td>
                            <td className="px-2 py-1 border">
                              <select className="px-1 py-1 border rounded text-xs" value={isOff ? row.off_type : row.punch_type}
                                onChange={e => { const v = e.target.value; const off = ['leave','absentee','holiday'].includes(v); setSmartAttRows(prev => prev.map((r, j) => j===i ? (off ? {...r, kind:'offday', off_type:v, is_warning: v!=='absentee'} : {...r, kind:'punch', punch_type:v}) : r)); }}>
                                <option value="in">In</option>
                                <option value="out">Out</option>
                                <option value="leave">Leave</option>
                                <option value="absentee">Absentee</option>
                                <option value="holiday">Holiday</option>
                              </select>
                            </td>
                            <td className="px-2 py-1 border">
                              {isOff ? <span className="text-gray-300 text-xs">—</span> : <UiTimeField value={row.time}
                                onChange={value => setSmartAttRows(prev => prev.map((r, j) => j===i ? {...r, time: value} : r))} />}
                            </td>
                            <td className="px-2 py-1 border text-center">
                              {isOff ? (
                                <select className="px-1 py-1 border rounded text-xs" value={row.is_warning ? 'warning' : 'deduct'} disabled={row.off_type==='holiday'}
                                  onChange={e => setSmartAttRows(prev => prev.map((r, j) => j===i ? {...r, is_warning: e.target.value==='warning'} : r))}>
                                  <option value="deduct">Deduct</option>
                                  <option value="warning">Warning</option>
                                </select>
                              ) : status==='half_day' ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">Half Day</span>
                                : status==='late' ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-700">Late</span>
                                : status==='on_time' ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">On Time</span>
                                : <span className="text-gray-400 text-xs">—</span>}
                            </td>
                            <td className="px-2 py-1 border min-w-[140px]">
                              <input className="w-full px-1 py-1 border rounded text-xs" value={row.notes} placeholder="Notes"
                                onChange={e => setSmartAttRows(prev => prev.map((r, j) => j===i ? {...r, notes: e.target.value} : r))} />
                            </td>
                            <td className="px-2 py-1 border text-center">
                              <button onClick={() => setSmartAttRows(prev => prev.filter((_,j) => j!==i))} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot><tr className="bg-gray-50">
                      <td colSpan={5} className="px-3 py-2 border text-sm text-gray-600">
                        {smartAttRows.length} record{smartAttRows.length!==1?'s':''}
                        {smartAttRows.filter(r=>!r.staffId).length>0 && <span className="ml-3 text-amber-600 text-xs font-medium">⚠ {smartAttRows.filter(r=>!r.staffId).length} unmatched — please select staff</span>}
                      </td>
                      <td className="px-3 py-2 border"></td>
                    </tr></tfoot>
                  </table>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowSmartAttModal(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
                  <button onClick={handleSmartAttSave} disabled={smartAttSaving || smartAttRows.filter(r=>r.staffId).length===0}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
                    {smartAttSaving ? 'Saving...' : <>Save {smartAttRows.filter(r=>r.staffId).length} Records</>}
                  </button>
                </div>
              </div>
            )}
            <div onMouseDown={startAttResize} className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize flex items-end justify-end select-none z-10 opacity-40 hover:opacity-100" title="Drag to resize"><svg width="10" height="10" viewBox="0 0 10 10"><path d="M9 1L1 9M9 4L4 9M9 7L7 9" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round"/></svg></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffManagement;
